import axios from 'axios';
import config from '../config';
import { Dialog } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getTalkingHeadVoiceConfig } from '../voicePreferences';
import { useAvatar } from '../AvatarContext';
import { HABC_RESPONSE_READOUT, QUESTION_READ_DELAY_MS } from './SubscaleQuiz';
import { HABC_SECTIONS as SECTIONS, HABC_RESPONSE_OPTIONS } from '../habcSubscales';

const QUESTIONNAIRES = [
  {
    id: 'habc',
    title: 'HABC Monitor',
    description: 'Healthy Aging Brain Care monitor',
    available: true,
  },
  // {
  //   id: 'npi',
  //   title: 'NPI',
  //   description: 'Neuropsychiatric Inventory',
  //   available: true,
  // },
  // {
  //   id: 'gad7',
  //   title: 'GAD-7',
  //   description: 'Generalized Anxiety Disorder scale',
  //   available: true,
  // },
  // {
  //   id: 'phq9',
  //   title: 'PHQ-9',
  //   description: 'Patient Health Questionnaire',
  //   available: true,
  // },
];

// The questionnaire list page is skipped — open straight into this questionnaire's detail.
const DEFAULT_QUESTIONNAIRE_ID =
  QUESTIONNAIRES.find((q) => q.available)?.id || QUESTIONNAIRES[0]?.id || null;

const ASSESSMENT_PERIODS = [
  { key: 'baseline', label: 'Baseline', offsetMonths: 0 },
  { key: 'month1', label: 'Month 1', offsetMonths: 1 },
  { key: 'month2', label: 'Month 2', offsetMonths: 2 },
  { key: 'month3', label: 'Month 3', offsetMonths: 3 },
];

const HABC_ASSESSMENT_PERIODS = [
  { key: 'baseline', label: 'Baseline', offsetDays: 0 },
  { key: 'week1', label: 'Week 1', offsetDays: 7 },
  { key: 'week2', label: 'Week 2', offsetDays: 14 },
  { key: 'week3', label: 'Week 3', offsetDays: 21 },
  { key: 'week4', label: 'Week 4', offsetDays: 28 },
  { key: 'week5', label: 'Week 5', offsetDays: 35 },
  { key: 'week6', label: 'Week 6', offsetDays: 42 },
];

// Full text of the HABC Caregiver Monitor Script (revised 06/27/23), page 1.
// `indent` mirrors the script's nesting; `parts` carry its bold/underline
// emphasis. The interviewer's name/role blanks and the physical response card
// are dropped because the caregiver completes this in the app, and
// "[specify name]" reads "your loved one".
const HABC_INTRO = [
  {
    parts: [
      "Hello. We would like to ask you some questions about your loved one's memory, mood, behaviors and daily activities. Specifically, we will be asking how often over the past two weeks your loved one has had a problem with certain symptoms. We will also be asking you a few questions about your own health.",
    ],
  },
  {
    parts: ['When considering your response to each question, please keep in mind these 3 things:'],
  },
  {
    indent: 1,
    parts: [
      'First - Your response should be your first reaction. Your gut instinct is more important than having actual evidence of a problem with any symptom.',
    ],
  },
  {
    indent: 1,
    parts: [
      'Second - There are no formal definitions for the symptoms. In general, whatever the term means to you is a reasonable and acceptable definition.',
    ],
  },
  {
    indent: 1,
    parts: [
      'Finally - and this is very important - we have a very specific definition for the word "problem" in these questions:',
    ],
  },
  {
    indent: 2,
    parts: [
      'A problem occurs only when your loved one has difficulty with the symptom ',
      { text: 'AND', bold: true, underline: true },
      ' he/she is distressed by that; ',
      { text: 'OR', bold: true },
      ' when your loved one has difficulty with the symptom AND ',
      { text: 'you', bold: true, underline: true },
      ' are distressed by that.',
    ],
  },
  {
    indent: 2,
    parts: [
      'For example, suppose your loved one is unable to do household chores, but you have hired someone to do the housework ',
      { text: 'AND', bold: true },
      ' both you and your loved one are comfortable with that arrangement. In this case, your loved one’s inability to do household chores would ',
      { text: 'not', bold: true, underline: true },
      ' be considered a problem - but what if you were able to hire a housekeeper, but your loved one did not like the person you hired and became agitated whenever the housekeeper was there? Then your loved one’s inability to do household chores ',
      { text: 'would be', bold: true, underline: true },
      ' considered a problem because it is causing either you or your loved one distress.',
    ],
  },
  {
    parts: [
      'You will be asked to rate the frequency of any problem over the past 2 weeks using a scale of:',
    ],
  },
];

// value matches HABC_RESPONSE_OPTIONS so the landing page can use the same
// answer-button colors (getStandardResponseClass).
const HABC_RESPONSE_GUIDE = [
  { value: 0, label: 'Not at all', days: '0-1 day' },
  { value: 1, label: 'Several days', days: '2-6 days' },
  { value: 2, label: 'More than half the days', days: '7-11 days' },
  { value: 3, label: 'Nearly every day', days: '12-14 days' },
];

const HABC_SUBSCALE_EXPLANATIONS = [
  {
    title: 'Cognitive Subscale',
    description: 'Tracks thinking, memory, judgment, repetition, orientation, appointments, and complex decision-making.',
    color: '#f8d7da',
  },
  {
    title: 'Functional Subscale',
    description: 'Tracks day-to-day abilities such as meals, medications, bathing, shopping, mobility, safety, housework, and quality of life.',
    color: '#fff3cd',
  },
  {
    title: 'Behavioral and Mood Subscale',
    description: 'Tracks mood, anxiety, agitation, impulsivity, appetite, sleep, hallucinations, suspiciousness, pacing, and repetitive behaviors.',
    color: '#d4edda',
  },
  {
    title: 'Caregiver Stress Subscale',
    description: 'Tracks the caregiver’s quality of life, financial future, mental health, and physical health.',
    color: '#cfe2ff',
  },
];

// Per "HABC Monitor Caregiver Staging" (Oct 2026), the HABC total score is the
// sum of all 31 items, caregiver stress included. Range 0-93.
const HABC_TOTAL_MAX_SCORE = 93;

const HABC_SCORE_BANDS = [
  { range: '0-4', label: 'Green status', color: 'green' },
  { range: '5-15', label: 'Yellow status', color: 'yellow' },
  { range: '16-25', label: 'Orange status', color: 'orange' },
  { range: `26-${HABC_TOTAL_MAX_SCORE}`, label: 'Red status', color: 'red' },
];

const GAD7_INTRO = [
  'This is calculated by assigning scores of 0, 1, 2, and 3 to the response categories, respectively, of “not at all,” “several days,” “more than half the days,” and “nearly every day.”',
  'GAD-7 total score for the seven items ranges from 0 to 21.',
];

const GAD7_SCORE_BANDS = [
  { range: '0-4', label: 'Minimal anxiety', color: 'green' },
  { range: '5-9', label: 'Mild anxiety', color: 'yellow' },
  { range: '10-14', label: 'Moderate anxiety', color: 'orange' },
  { range: '15-21', label: 'Severe anxiety', color: 'red' },
];

const GAD7_SECTIONS = [
  {
    title: 'GAD-7 Anxiety',
    color: '#e7f3fb',
    prompt: 'Over the last two weeks, how often have you been bothered by the following problems?',
    items: [
      'Feeling nervous, anxious, or on edge',
      'Not being able to stop or control worrying',
      'Worrying too much about different things',
      'Trouble relaxing',
      'Being so restless that it is hard to sit still',
      'Becoming easily annoyed or irritable',
      'Feeling afraid, as if something awful might happen',
    ],
  },
];

const PHQ9_INTRO = [
  'Patient Health Questionnaire-9 (PHQ-9).',
  'Over the last 2 weeks, answer how often you have been bothered by each problem.',
  'The nine scored items use 0, 1, 2, and 3 for “not at all,” “several days,” “more than half the days,” and “nearly every day.”',
  'PHQ-9 total score for the nine items ranges from 0 to 27.',
];

const PHQ9_SECTIONS = [
  {
    title: 'PHQ-9',
    color: '#eef7ee',
    prompt: 'Over the last 2 weeks, how often have you been bothered by any of the following problems?',
    items: [
      'Little interest or pleasure in doing things',
      'Feeling down, depressed, or hopeless',
      'Trouble falling or staying asleep, or sleeping too much',
      'Feeling tired or having little energy',
      'Poor appetite or overeating',
      'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
      'Trouble concentrating on things, such as reading the newspaper or watching television',
      'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
      'Thoughts that you would be better off dead or of hurting yourself in some way',
    ],
  },
];

const HABC_SELECTED_RESPONSE_CLASSES = {
  0: 'border-emerald-600 bg-emerald-500 text-white shadow-md shadow-emerald-100',
  1: 'border-yellow-500 bg-yellow-300 text-[#3d2b00] shadow-md shadow-yellow-100',
  2: 'border-orange-600 bg-orange-500 text-white shadow-md shadow-orange-100',
  3: 'border-red-600 bg-red-500 text-white shadow-md shadow-red-100',
};

// Answer-option colors for the HABC scale (0-3). Shared by the answer buttons
// and the landing page's response-scale legend so they always match.
const HABC_RESPONSE_COLOR_CLASSES = {
  0: 'border-emerald-600 bg-emerald-500 text-white',
  1: 'border-yellow-500 bg-yellow-300 text-[#3d2b00]',
  2: 'border-orange-600 bg-orange-500 text-white',
  3: 'border-red-600 bg-red-500 text-white',
};

// Score bands on the landing page use the answer-option palette so the
// colors mean the same thing everywhere (green = best ... red = most severe).
const HABC_BAND_COLOR_VALUE = { green: 0, yellow: 1, orange: 2, red: 3 };

const HABC_RESPONSE_SHADOW_CLASSES = {
  0: 'shadow-md shadow-emerald-100',
  1: 'shadow-md shadow-yellow-100',
  2: 'shadow-md shadow-orange-100',
  3: 'shadow-md shadow-red-100',
};

const getStandardResponseClass = (questionnaireId, isSelected, value) => {
  if (questionnaireId === 'habc') {
    const base = HABC_RESPONSE_COLOR_CLASSES[value]
      ? `${HABC_RESPONSE_COLOR_CLASSES[value]} ${HABC_RESPONSE_SHADOW_CLASSES[value]}`
      : 'border-[#006298] bg-[#dff0fc] text-[#06354f]';

    const selectedRing = isSelected
      ? 'ring-4 ring-[#006298]/30 scale-[1.01]'
      : 'opacity-90 hover:opacity-100';

    return `${base} ${selectedRing}`;
  }

  if (!isSelected) {
    return 'border-gray-300 bg-white text-[#1c3f52] hover:border-[#006298]';
  }

  return 'border-[#006298] bg-[#dff0fc] text-[#06354f]';
};

const GAD7_RESPONSE_OPTIONS = [
  { label: 'Not at all (0)', value: 0 },
  { label: 'Several days (1)', value: 1 },
  { label: 'More than half the days (2)', value: 2 },
  { label: 'Nearly every day (3)', value: 3 },
];

const PHQ9_RESPONSE_OPTIONS = GAD7_RESPONSE_OPTIONS;

const NPI_SECTIONS = [
  {
    title: 'Delusions',
    explanation: 'Beliefs the patient is convinced are true even though they are not, such as people trying to harm or steal from them, family members not being who they say they are, or the house not being their home.',
    screener: 'Does the patient have any beliefs that you know are not true - for example, insisting that people are trying to harm him/her or steal from him/her? Has s/he said that family members are not who they say they are or that the house is not their home?',
    convictionQuestion: 'I am interested if the patient is convinced that these things are happening to him/her, not just suspiciousness. Do you think s/he is convinced?',
    followUpItems: [
      'Does patient believe s/he is in danger - that others are planning to hurt him/her?',
      'Does patient believe that others are stealing from him/her?',
      'Does patient believe that her/his spouse is having an affair?',
      'Does patient believe that unwelcome guests are living in her/his house?',
      'Does patient believe that her/his spouse or others are not who they claim to be?',
      'Does patient believe that her/his house is not her/his home?',
      'Does patient believe that family members plan to abandon him/her?',
      'Does patient believe that television or magazine figures are actually present in the home? Does s/he try to talk or interact with them?',
      'Does s/he believe in any other unusual things that I have not asked about?',
    ],
    frequencyQuestion: 'How often does s/he experience beliefs that are not true?',
    severityQuestion: 'How severe would you say it is?',
    severityDescriptions: [
      '1 = Mild - delusions are present but seem harmless and produce little distress in him/her.',
      '2 = Moderate - delusions are distressing and disruptive.',
      '3 = Marked - delusions are very disruptive and are a major source of behavioral disruption. If PRN medications are prescribed, their use signals that the delusions are of marked severity.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Hallucinations',
    explanation: 'False visions, voices, or other experiences that are not present, such as seeing people, hearing voices, or sensing things that others do not.',
    screener: 'Does the patient have hallucinations such as false visions or voices? Does s/he seem to see, hear, or experience things that are not present?',
    convictionQuestion: 'By this question we do not mean just mistaken beliefs such as stating that someone who has died is still alive; rather we are asking if s/he has abnormal experiences of sounds or visions.',
    followUpItems: [
      'Does the patient describe hearing voices or act as if s/he hears voices?',
      'Does the patient talk to people who are not there?',
      'Does the patient describe seeing things not seen by others or behave as if s/he is seeing things not seen by others like people, animals, lights, etc.?',
      'Does the patient report smelling odors not smelled by others?',
      'Does the patient describe feeling things on her/his skin or otherwise appear to be feeling things crawling or touching him/her?',
      'Does the patient describe tastes that are without any known causes?',
      'Does the patient describe any other unusual sensory experiences?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say it is?',
    severityDescriptions: [
      '1 = Mild - hallucinations are present but harmless and cause little distress for him/her.',
      '2 = Moderate - hallucinations are distressing and disruptive to him/her.',
      '3 = Marked - hallucinations are very disruptive and are a major source of behavioral disruption; PRN meds may be required to control them.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Agitation/Aggression',
    explanation: 'Periods when the patient refuses to cooperate, resists help, becomes hard to handle, argues, shouts, or behaves aggressively.',
    screener: 'Does the patient have periods when s/he refuses to cooperate or will not let people help him/her? Is s/he hard to handle?',
    followUpItems: [
      'Does the patient get upset with those trying to care for him/her or resist activities such as bathing or changing clothes?',
      'Is the patient stubborn, having to have things her/his way?',
      'Is the patient uncooperative, resisting help from others?',
      'Does the patient curse or shout angrily?',
      'Does the patient slam the doors, kick furniture, or throw things?',
      'Does the patient attempt to hurt or hit others?',
      'Does the patient have any other behaviors that make him/her hard to handle?',
      'Does the patient have any other aggressive or agitated behaviors?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say that it is?',
    severityDescriptions: [
      '1 = Mild - behavior is disruptive but can be managed with redirection or reassurance.',
      '2 = Moderate - behavior is disruptive and difficult to redirect or control him/her.',
      '3 = Marked - agitation is very disruptive and a major source of difficulty; there may be a threat of personal harm; medications are often required.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Depression/Dysphoria',
    explanation: 'Sad, depressed, tearful, hopeless, guilty, or self-critical mood symptoms observed or reported by the patient.',
    screener: 'Does the patient seem sad or depressed? Does s/he say that s/he feels sad or depressed?',
    followUpItems: [
      'Does the patient have periods of tearfulness or sobbing that seem to indicate sadness?',
      'Does the patient say or act as if s/he is sad or in low spirits?',
      'Does the patient put him/herself down or say that s/he feels like a failure?',
      'Does the patient say that s/he is a bad person or deserves to be punished?',
      'Does the patient seem very discouraged or say that s/he has no future?',
      'Does the patient say that s/he is a burden to the family or that the family would be better off without him/her?',
      'Does the patient express a wish for death or talk about killing him/herself?',
      'Does the patient show any other signs of depression or sadness?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say it is?',
    severityDescriptions: [
      '1 = Mild - depression is distressing but usually responds to redirection or reassurance.',
      '2 = Moderate - depression is distressing, depressive symptoms are spontaneously voiced by him/her and difficult to alleviate.',
      '3 = Marked - depression is very distressing and a major source of suffering for him/her.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Anxiety',
    explanation: 'Nervousness, worry, fearfulness, panic, tension, or fear of being separated from the caregiver without an obvious reason.',
    screener: 'Is the patient very nervous, worried, or frightened for no apparent reason? Does s/he seem very tense or fidgety? Is s/he afraid to be away from you?',
    followUpItems: [
      'Does the patient say that s/he is worried about planned events?',
      'Does the patient have periods of feeling shaky, unable to relax, or feeling excessively tense?',
      'Does the patient have periods or complain of shortness of breath, gasping, or sighing for no apparent reason other than nervousness?',
      'Does the patient complain of butterflies in her/his stomach, or of racing or pounding of the heart in association with nervousness? Symptoms not explained by ill health?',
      'Does the patient avoid certain places or situations that make him/her more nervous such as riding in the car, meeting with friends, or being in crowds?',
      'Does the patient become nervous or upset when separated from you? Does s/he cling to you to keep from being separated?',
      'Does the patient show any other signs of anxiety?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say that it is?',
    severityDescriptions: [
      '1 = Mild - anxiety is distressing but usually responds to redirection or reassurance.',
      '2 = Mild - anxiety is distressing, anxiety symptoms are spontaneously voiced by the patient and difficult to alleviate.',
      '3 = Marked - anxiety is very distressing and a major source of suffering for him/her.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Elation/Euphoria',
    explanation: 'An unusually elevated or overly cheerful mood, laughing too easily, acting excessively happy, or seeming too good for no clear reason.',
    screener: 'Does the patient seem too cheerful or too happy for no reason?',
    convictionQuestion: 'I do not mean the normal happiness that comes from seeing friends, receiving presents, or spending time with family members. I am asking if s/he has a persistent and abnormally good mood or finds humor where others do not.',
    followUpItems: [
      'Does the patient appear to feel too good or to be too happy, different from her/his usual self?',
      'Does the patient find humor and laugh at things that others do not find funny?',
      'Does the patient seem to have a childish sense of humor with a tendency to giggle or laugh inappropriately, such as when something unfortunate happens to others?',
      'Does the patient tell jokes or make remarks that have little humor for others but seem funny for him/her?',
      'Does the patient play childish pranks such as pinching or playing "keep away" for the fun of it?',
      'Does the patient "talk big" or claim to have more wealth or abilities than is true?',
      'Does the patient show any other signs of feeling too good or being too happy?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say that it is?',
    severityDescriptions: [
      '1 = Mild - elation is notable to friends and family, but is not disruptive.',
      '2 = Moderate - elation is notably abnormal.',
      '3 = Marked - elation is very pronounced; s/he is euphoric and finds nearly everything to be humorous.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Apathy/Indifference',
    explanation: 'Reduced interest, motivation, emotional response, initiative, conversation, or participation in usual activities.',
    screener: 'Has the patient lost interest in the world around him/her? Has s/he lost interest in doing things or lack of motivation for starting new activities? Is s/he more difficult to engage in conversation or doing chores? Is the patient apathetic or indifferent?',
    followUpItems: [
      'Does the patient seem less spontaneous and less active than usual?',
      'Is the patient less likely to initiate a conversation?',
      'Is the patient less affectionate or lacking in emotions when compared to her/his usual self?',
      'Does the patient contribute less to household chores?',
      'Does the patient seem less interested in the activities and plans of others?',
      'Has the patient lost interest in friends or family members?',
      'Is the patient less enthusiastic about her/his usual interests?',
      'Does the patient show any other signs that s/he does not care about doing new things?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say that it is?',
    severityDescriptions: [
      '1 = Mild - apathy is notable but produces little interference with daily routines; only mildly different from patient\'s usual behavior; patient responds to suggestions to engage in activities.',
      '2 = Moderate - apathy is very evident; may be overcome by the caregiver with coaxing and encouragement; responds spontaneously only to powerful events such as visits from close relatives or family members.',
      '3 = Marked - apathy is very evident and usually fails to respond to any encouragement or external events.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Disinhibition',
    explanation: 'Impulsive behavior without thinking, such as saying or doing socially inappropriate, embarrassing, or unusually personal things.',
    screener: 'Does the patient seem to act impulsively without thinking? Does s/he do or say things that are not usually done or said in public? Does s/he do things that are embarrassing to you or others?',
    followUpItems: [
      'Does the patient act impulsively without appearing to consider the consequences?',
      'Does the patient talk to total strangers as if s/he knew them?',
      'Does the patient say things to people that are insensitive or hurt their feelings?',
      'Does the patient say crude things or make sexual remarks that they would not have usually said?',
      'Does the patient talk openly about very personal or private matters not usually discussed in public?',
      'Does the patient take liberties or touch or hug others in a way that is out of character for him/her?',
      'Does the patient show any other signs of loss of control of her/his impulses?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say that it is?',
    severityDescriptions: [
      '1 = Mild - disinhibition is notable but usually responds to redirection and guidance.',
      '2 = Moderate - disinhibition is very evident and difficult to overcome by the caregiver.',
      '3 = Marked - disinhibition usually fails to respond to any intervention by the caregiver and is a source of embarrassment or social distress.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Irritability/Lability',
    explanation: 'Irritability, impatience, mood swings, quick temper, difficulty coping with delays, or sudden changes in emotion.',
    screener: 'Does the patient get irritated and easily disturbed? Are her/his moods very changeable? Is s/he abnormally impatient?',
    convictionQuestion: 'We do not mean frustration over memory loss or inability to perform usual tasks; we are interested to know if the patient has abnormal irritability, impatience, or rapid emotional changes different from her/his usual self.',
    followUpItems: [
      'Does the patient have a bad temper, flying "off the handle" easily over little things?',
      'Does the patient rapidly change moods from one to another, being fine one minute and angry the next?',
      'Does the patient have sudden flashes of anger?',
      'Is the patient impatient, having trouble coping with delays or waiting for planned activities?',
      'Is the patient cranky and irritable?',
      'Is the patient argumentative and difficult to get along with?',
      'Does the patient show any other signs of irritability?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say it is?',
    severityDescriptions: [
      '1 = Mild - irritability or lability is notable but usually responds to redirection and reassurance.',
      '2 = Moderate - irritability and lability are very evident and difficult to overcome by the caregiver.',
      '3 = Marked - irritability and lability are very evident, they usually fail to respond to any intervention by the caregiver and they are a major source of distress.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Aberrant Motor Behavior',
    explanation: 'Repeated motor behaviors such as pacing, wandering, rummaging, opening drawers, picking at things, or doing activities over and over.',
    screener: 'Does the patient pace, do things over and over such as opening closets or drawers, or repeatedly pick at things or wind string or threads?',
    followUpItems: [
      'Does the patient pace around the house without apparent purpose?',
      'Does the patient rummage around opening and unpacking drawers or closets?',
      'Does the patient repeatedly put on and take off clothing?',
      'Does the patient have repetitive activities or "habits" that s/he performs over and over?',
      'Does the patient engage in repetitive activities such as handling buttons, picking, wrapping string, etc.?',
      'Does the patient fidget excessively, seem unable to sit still, or bounce her/his feet or tap her/his fingers a lot?',
      'Does the patient do any other activities over and over?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say that it is?',
    severityDescriptions: [
      '1 = Mild - abnormal motor activity is notable but produces little interference with daily routines.',
      '2 = Moderate - abnormal motor activity is very evident; can be overcome by the caregiver.',
      '3 = Marked - abnormal motor activity is very evident, it usually fails to respond to any intervention by the caregiver and is a major source of distress.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Sleep',
    explanation: 'Sleep-related disturbance such as trouble sleeping, getting up at night, wandering at night, dressing at night, or waking others.',
    screener: 'Does the patient have difficulty sleeping? Do not count if the patient simply gets up once or twice per night only to go to the bathroom and falls back asleep immediately. Is s/he up at night? Does s/he wander at night, get dressed, or disturb your sleep?',
    followUpItems: [
      'Does the patient have difficulty falling asleep?',
      'Does the patient get up during the night? Do not count if the patient gets up once or twice only to go to the bathroom and falls back asleep immediately.',
      'Does the patient wander, pace, or get involved in inappropriate activities at night?',
      'Does the patient awaken you during the night?',
      'Does the patient awaken at night, dress, and plan to go out thinking that it is morning and time to start the day?',
      'Does the patient awaken too early in the morning, earlier than was her/his habit?',
      'Does the patient sleep excessively during the day?',
      'Does the patient have any other night-time behaviors that bother you that we have not talked about?',
    ],
    frequencyQuestion: 'How often does this occur?',
    severityQuestion: 'How severe would you say that it is?',
    severityDescriptions: [
      '1 = Mild - night-time behaviors occur but they are not particularly disruptive.',
      '2 = Moderate - night-time behaviors occur and disturb the patient and the sleep of the caregiver; more than one type of night-time behavior may be present.',
      '3 = Marked - night-time behaviors occur; several types may be present; the patient is very distressed during the night and the caregiver\'s sleep is markedly disturbed.',
    ],
    distressQuestion: 'How emotionally distressing do you find this behavior?',
  },
  {
    title: 'Appetite and Eating Disorders',
    explanation: 'Changes in appetite, weight, eating behavior, food preferences, table manners, or eating unusual amounts or types of food.',
  },
];

const NPI_PRESENT_OPTIONS = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 0 },
];

const NPI_FREQUENCY_OPTIONS = [
  { label: 'Occasionally (1)', value: 1 },
  { label: 'Often (2)', value: 2 },
  { label: 'Frequently (3)', value: 3 },
  { label: 'Very frequently (4)', value: 4 },
];

const NPI_SEVERITY_OPTIONS = [
  { label: 'Mild (1)', value: 1 },
  { label: 'Moderate (2)', value: 2 },
  { label: 'Marked (3)', value: 3 },
];

const NPI_DISTRESS_OPTIONS = [
  { label: 'Not at all (0)', value: 0 },
  { label: 'Minimally (1)', value: 1 },
  { label: 'Mildly (2)', value: 2 },
  { label: 'Moderately (3)', value: 3 },
  { label: 'Severely (4)', value: 4 },
  { label: 'Very severely (5)', value: 5 },
];

const HABC_SUBSCALE_SCORE_RULES = [
  {
    maxScore: 18,
    bands: [
      { max: 1, label: 'Normal range', color: 'green' },
      { max: 4, label: 'Mild symptoms', color: 'yellow' },
      { max: 11, label: 'Moderate symptoms', color: 'orange' },
      { max: Infinity, label: 'Severe symptoms', color: 'red' },
    ],
  },
  {
    maxScore: 33,
    bands: [
      { max: 1, label: 'Normal range', color: 'green' },
      { max: 6, label: 'Mild symptoms', color: 'yellow' },
      { max: 11, label: 'Moderate symptoms', color: 'orange' },
      { max: Infinity, label: 'Severe symptoms', color: 'red' },
    ],
  },
  {
    maxScore: 30,
    bands: [
      { max: 4, label: 'Normal range', color: 'green' },
      { max: 9, label: 'Mild symptoms', color: 'yellow' },
      { max: 19, label: 'Moderate symptoms', color: 'orange' },
      { max: Infinity, label: 'Severe symptoms', color: 'red' },
    ],
  },
  {
    maxScore: 12,
    bands: [
      { max: 0, label: 'Normal range', color: 'green' },
      { max: 4, label: 'Mild symptoms', color: 'yellow' },
      { max: 8, label: 'Moderate symptoms', color: 'orange' },
      { max: Infinity, label: 'Severe symptoms', color: 'red' },
    ],
  },
];

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const addCalendarMonths = (date, months) => {
  const source = startOfDay(date);
  const target = new Date(source);
  const originalDay = source.getDate();

  target.setMonth(source.getMonth() + months);

  if (target.getDate() !== originalDay) {
    target.setDate(0);
  }

  return startOfDay(target);
};

const addCalendarDays = (date, days) => {
  const target = startOfDay(date);
  target.setDate(target.getDate() + days);
  return target;
};

const formatDueDate = (date) =>
  date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const getReportQuestionnaireId = (report) =>
  String(report?.questionnaireType || report?.questionnaireId || 'habc').toLowerCase();

const getAssessmentPeriods = (questionnaireId) =>
  questionnaireId === 'habc' ? HABC_ASSESSMENT_PERIODS : ASSESSMENT_PERIODS;

const getNormalizedPeriodKey = (questionnaireId, periodKey, fallbackKey) => {
  const key = periodKey || fallbackKey;

  if (questionnaireId !== 'habc') {
    return key;
  }

  const legacyHabcKeys = {
    month1: 'week2',
    month2: 'week4',
    month3: 'week6',
  };

  return legacyHabcKeys[key] || key;
};

const getQuestionnaireSchedule = (questionnaire, reports) => {
  const assessmentPeriods = getAssessmentPeriods(questionnaire.id);
  const questionnaireReports = reports
    .filter((report) => getReportQuestionnaireId(report) === questionnaire.id)
    .sort((first, second) => new Date(first.createdAt) - new Date(second.createdAt));

  const completedKeys = new Set(
    questionnaireReports.map((report, index) =>
      getNormalizedPeriodKey(questionnaire.id, report.periodKey, assessmentPeriods[index]?.key)
    )
  );
  const baselineReport = questionnaireReports.find((report) => (report.periodKey || 'baseline') === 'baseline') || questionnaireReports[0];
  const today = startOfDay(new Date());
  const baselineDate = baselineReport?.createdAt ? startOfDay(new Date(baselineReport.createdAt)) : today;

  const periods = assessmentPeriods.map((period) => {
    const completed = completedKeys.has(period.key);
    const dueDate = period.offsetDays != null
      ? addCalendarDays(baselineDate, period.offsetDays)
      : addCalendarMonths(baselineDate, period.offsetMonths);
    const daysUntilDue = Math.ceil((dueDate - today) / DAY_MS);

    return {
      ...period,
      completed,
      dueDate,
      daysUntilDue,
    };
  });

  const nextPeriod = periods.find((period) => !period.completed);
  const dueNow = Boolean(nextPeriod && nextPeriod.daysUntilDue <= 0);
  const statusText = !nextPeriod
    ? 'Completed'
    : dueNow
      ? 'Due now'
      : `Due in ${nextPeriod.daysUntilDue} day${nextPeriod.daysUntilDue === 1 ? '' : 's'}`;

  return {
    questionnaire,
    reports: questionnaireReports,
    periods,
    nextPeriod,
    dueNow,
    statusText,
  };
};

const getGad7Severity = (score) => {
  if (score >= 15) return 'Severe anxiety';
  if (score >= 10) return 'Moderate anxiety';
  if (score >= 5) return 'Mild anxiety';
  return 'Minimal anxiety';
};

const getGad7SeverityColor = (scoreOrSeverity) => {
  const severity = typeof scoreOrSeverity === 'number'
    ? getGad7Severity(scoreOrSeverity)
    : String(scoreOrSeverity || '');

  if (severity === 'Severe anxiety') return 'bg-[#fde8e8] text-[#9b2424] border-[#f3b8b8]';
  if (severity === 'Moderate anxiety') return 'bg-[#fff0dc] text-[#9a5800] border-[#f2c180]';
  if (severity === 'Mild anxiety') return 'bg-[#fff8d7] text-[#7a6100] border-[#eadb82]';
  return 'bg-[#e5f6ea] text-[#1f7a3b] border-[#b8e0c3]';
};

const getGad7BandColor = (color) => {
  if (color === 'red') return 'border-[#f3b8b8] bg-[#fde8e8] text-[#9b2424]';
  if (color === 'orange') return 'border-[#f2c180] bg-[#fff0dc] text-[#9a5800]';
  if (color === 'yellow') return 'border-[#eadb82] bg-[#fff8d7] text-[#7a6100]';
  return 'border-[#b8e0c3] bg-[#e5f6ea] text-[#1f7a3b]';
};

const getHabcStatus = (score) => {
  if (score >= 26) return 'Red status';
  if (score >= 16) return 'Orange status';
  if (score >= 5) return 'Yellow status';
  return 'Green status';
};

const getHabcStatusColor = (scoreOrStatus) => {
  const status = typeof scoreOrStatus === 'number'
    ? getHabcStatus(scoreOrStatus)
    : String(scoreOrStatus || '');

  if (status === 'Red status') return 'bg-[#fde8e8] text-[#9b2424] border-[#f3b8b8]';
  if (status === 'Orange status') return 'bg-[#fff0dc] text-[#9a5800] border-[#f2c180]';
  if (status === 'Yellow status') return 'bg-[#fff8d7] text-[#7a6100] border-[#eadb82]';
  return 'bg-[#e5f6ea] text-[#1f7a3b] border-[#b8e0c3]';
};

const parseReportScore = (report, sections, questionnaireId) => {
  // Some older HABC reports stored a total without the caregiver stress items,
  // so recompute from the raw answers instead of trusting report.total.
  const isHabc = questionnaireId === 'habc';
  if (!isHabc) {
    const totalText = String(report?.total || '');
    const totalMatch = totalText.match(/^\s*(\d+)/);
    if (totalMatch) {
      return Number(totalMatch[1]);
    }
  }

  return sections.reduce((sum, section, sectionIndex) => {
    return sum + (section.items || []).reduce((sectionSum, _, itemIndex) =>
      sectionSum + (Number(report?.answers?.[`sec${sectionIndex}_q${itemIndex}`]) || 0)
    , 0);
  }, 0);
};

const getReportStatus = (questionnaireId, report) => {
  const sections = getQuestionnaireSections(questionnaireId);
  const score = parseReportScore(report, sections, questionnaireId);

  if (questionnaireId === 'gad7') {
    return {
      label: report?.severity || getGad7Severity(score),
      score,
      colorClass: getGad7SeverityColor(report?.severity || score),
    };
  }

  if (questionnaireId === 'habc') {
    return {
      label: getHabcStatus(score),
      score,
      colorClass: getHabcStatusColor(score),
    };
  }

  return {
    label: `Score ${score}`,
    score,
    colorClass: 'bg-[#eef3f6] text-[#526b7a] border-[#d7e3ea]',
  };
};

// Zones and descriptions (Oct 2026 update): <5 stable or low burden,
// 5-15 mild, 16-25 moderate, >25 severe burden.
const getHabcStatusDescription = (score) => {
  if (score >= 26) return 'Severe burden';
  if (score >= 16) return 'Moderate burden';
  if (score >= 5) return 'Mild burden';
  return 'Stable or low burden';
};

const getHabcGaugeColor = (score) => {
  if (score >= 26) return '#e64636';
  if (score >= 16) return '#f28a35';
  if (score >= 5) return '#f4e95e';
  return '#4fa35a';
};

const getHabcGaugeTextColor = (score) => {
  if (score >= 5 && score < 16) return '#3d2b00';
  return '#ffffff';
};

const getHabcGaugeArcColor = (color) => {
  if (color === 'red') return '#e64636';
  if (color === 'orange') return '#f28a35';
  if (color === 'yellow') return '#f4e95e';
  return '#4fa35a';
};

const getHabcSubscaleStatus = (sectionIndex, score) => {
  const rule = HABC_SUBSCALE_SCORE_RULES[sectionIndex];
  const band = rule?.bands.find((item) => score <= item.max) || rule?.bands[rule.bands.length - 1];

  return {
    label: band?.label || 'Normal range',
    color: band?.color || 'green',
    maxScore: rule?.maxScore || 0,
  };
};

const getHabcSubscaleBadgeClass = (color) => {
  if (color === 'red') return 'border-[#f3b8b8] bg-[#fde8e8] text-[#9b2424]';
  if (color === 'orange') return 'border-[#f2c180] bg-[#fff0dc] text-[#9a5800]';
  if (color === 'yellow') return 'border-[#eadb82] bg-[#fff8d7] text-[#7a6100]';
  return 'border-[#b8e0c3] bg-[#e5f6ea] text-[#1f7a3b]';
};

const getHabcSubscaleCardClass = (color) => {
  if (color === 'red') return 'border-[#f3b8b8] bg-[#fff5f5]';
  if (color === 'orange') return 'border-[#f2c180] bg-[#fff8ef]';
  if (color === 'yellow') return 'border-[#eadb82] bg-[#fffdf0]';
  return 'border-[#b8e0c3] bg-[#f5fff7]';
};

const getPointOnCircle = (cx, cy, radius, angle) => {
  const radians = (angle * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
};

const getGaugeAngle = (score, maxScore = HABC_TOTAL_MAX_SCORE) =>
  140 + (Math.max(0, Math.min(maxScore, score)) / maxScore) * 260;

const getGaugeArcPath = (startScore, endScore, radius = 118, maxScore = HABC_TOTAL_MAX_SCORE) => {
  const cx = 160;
  const cy = 160;
  const startAngle = getGaugeAngle(startScore, maxScore);
  const endAngle = getGaugeAngle(endScore, maxScore);
  const start = getPointOnCircle(cx, cy, radius, startAngle);
  const end = getPointOnCircle(cx, cy, radius, endAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
};

const HabcScoreGauge = ({ score }) => {
  const clampedScore = Math.max(0, Math.min(HABC_TOTAL_MAX_SCORE, score));
  const scoreAngle = getGaugeAngle(clampedScore);
  const needleEnd = getPointOnCircle(160, 160, 92, scoreAngle);
  const status = getHabcStatus(score);
  const description = getHabcStatusDescription(score);
  const needleColor = getHabcGaugeColor(score);
  const ticks = [0, 4, 15, 25, HABC_TOTAL_MAX_SCORE];

  return (
    <div className="rounded-2xl border border-[#d7e7f0] bg-white p-5 shadow-sm">
      <div className="mb-1 text-center text-[1.6rem] font-extrabold text-[#0f2b3a]">Total Score</div>
      <div className="mx-auto max-w-[460px]">
        <svg viewBox="0 0 320 285" role="img" aria-label={`HABC total score ${score} out of ${HABC_TOTAL_MAX_SCORE}`} className="h-auto w-full">
          <path d={getGaugeArcPath(0, HABC_TOTAL_MAX_SCORE, 132)} fill="none" stroke="#b7b7b7" strokeWidth="18" strokeLinecap="round" />
          {HABC_SCORE_BANDS.map((band) => {
            const [startText, endText] = band.range.split('-');
            const start = Number(startText);
            const end = Number(endText);
            return (
              <path
                key={band.label}
                d={getGaugeArcPath(start, end)}
                fill="none"
                stroke={getHabcGaugeArcColor(band.color)}
                strokeWidth="32"
                strokeLinecap="butt"
              />
            );
          })}
          {ticks.map((tick) => {
            const outer = getPointOnCircle(160, 160, 118, getGaugeAngle(tick));
            const inner = getPointOnCircle(160, 160, 105, getGaugeAngle(tick));
            const label = getPointOnCircle(160, 160, 78, getGaugeAngle(tick));
            return (
              <g key={tick}>
                <line x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="#555" strokeWidth="3" strokeLinecap="round" />
                <text x={label.x} y={label.y + 6} textAnchor="middle" className="fill-[#26343d] text-[22px] font-extrabold">
                  {tick}
                </text>
              </g>
            );
          })}
          <line x1="160" y1="160" x2={needleEnd.x} y2={needleEnd.y} stroke="#5f5f5f" strokeWidth="7" strokeLinecap="round" />
          <line x1="160" y1="160" x2={needleEnd.x} y2={needleEnd.y} stroke={needleColor} strokeWidth="4" strokeLinecap="round" />
          <circle cx="160" cy="160" r="20" fill="#202020" stroke="#0b0b0b" strokeWidth="2" />
          <circle cx="160" cy="160" r="8" fill="#ffffff" opacity="0.18" />
          <text x="160" y="245" textAnchor="middle" className="fill-[#061927] text-[42px] font-black">
            {score}
          </text>
        </svg>
      </div>
      <div className="text-center">
        <div className="text-[1.5rem] font-extrabold text-[#0f3950]">{description}</div>
        <div className={`mt-2 inline-flex rounded-full border px-4 py-1.5 text-[1.1rem] font-extrabold ${getHabcStatusColor(status)}`}>
          {status} · {score}/{HABC_TOTAL_MAX_SCORE}
        </div>
      </div>
    </div>
  );
};

const HabcSubscaleGauge = ({ title, score, sectionIndex, onTipsClick, tipsDisabled }) => {
  const status = getHabcSubscaleStatus(sectionIndex, score);
  const maxScore = status.maxScore;
  const clampedScore = Math.max(0, Math.min(maxScore, score));
  const scoreAngle = getGaugeAngle(clampedScore, maxScore);
  const needleEnd = getPointOnCircle(160, 160, 83, scoreAngle);
  const rule = HABC_SUBSCALE_SCORE_RULES[sectionIndex];
  const bands = rule?.bands || [];
  const tickValues = [...new Set([
    0,
    ...bands
      .filter((band) => Number.isFinite(band.max) && band.max > 0 && band.max < maxScore)
      .map((band) => band.max),
    maxScore,
  ])];
  let bandStart = 0;

  return (
    <div className={`rounded-2xl border p-3 shadow-sm ${getHabcSubscaleCardClass(status.color)}`}>
      <div className="mb-1 text-center text-[1.3rem] font-extrabold text-[#0f2b3a]">{title}</div>
      <svg viewBox="0 0 320 250" role="img" aria-label={`${title} score ${score} out of ${maxScore}`} className="mx-auto h-auto w-full max-w-[250px]">
        <path d={getGaugeArcPath(0, maxScore, 110, maxScore)} fill="none" stroke="#b7b7b7" strokeWidth="16" strokeLinecap="round" />
        {bands.map((band) => {
          const end = band.max === Infinity ? maxScore : Math.min(maxScore, band.max);
          const start = bandStart;
          bandStart = end;
          if (end <= start) return null;

          return (
            <path
              key={band.label}
              d={getGaugeArcPath(start, end, 98, maxScore)}
              fill="none"
              stroke={getHabcGaugeArcColor(band.color)}
              strokeWidth="28"
              strokeLinecap="butt"
            />
          );
        })}
        {tickValues.map((tick) => {
          const outer = getPointOnCircle(160, 160, 100, getGaugeAngle(tick, maxScore));
          const inner = getPointOnCircle(160, 160, 90, getGaugeAngle(tick, maxScore));
          const label = getPointOnCircle(160, 160, 68, getGaugeAngle(tick, maxScore));
          return (
            <g key={tick}>
              <line x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="#555" strokeWidth="2.5" strokeLinecap="round" />
              <text x={label.x} y={label.y + 5} textAnchor="middle" className="fill-[#26343d] text-[19px] font-extrabold">
                {tick}
              </text>
            </g>
          );
        })}
        <line x1="160" y1="160" x2={needleEnd.x} y2={needleEnd.y} stroke="#5f5f5f" strokeWidth="6" strokeLinecap="round" />
        <line x1="160" y1="160" x2={needleEnd.x} y2={needleEnd.y} stroke={getHabcGaugeArcColor(status.color)} strokeWidth="3.5" strokeLinecap="round" />
        <circle cx="160" cy="160" r="17" fill="#202020" stroke="#0b0b0b" strokeWidth="2" />
        <text x="160" y="228" textAnchor="middle" className="fill-[#061927] text-[30px] font-black">
          {score}/{maxScore}
        </text>
      </svg>
      <div className="text-center">
        <span className={`inline-flex rounded-full border px-3.5 py-1.5 text-[1.05rem] font-extrabold ${getHabcSubscaleBadgeClass(status.color)}`}>
          {status.label}
        </span>
      </div>
      <div className="mt-3 text-center">
        <button
          type="button"
          className="vica-btn vica-btn-md !text-[1.1rem]"
          onClick={() => onTipsClick(sectionIndex)}
          disabled={tipsDisabled}
        >
          Handout Tips
        </button>
      </div>
    </div>
  );
};

const getHabcSubscaleHandoutTips = (tipsData, report, sectionIndex) => {
  if (!tipsData || !report?.answers) {
    return [];
  }

  const handouts = new Map();
  const section = SECTIONS[sectionIndex];

  (section?.items || []).forEach((question, itemIndex) => {
    const answerValue = report.answers[`sec${sectionIndex}_q${itemIndex}`];
    const rule = tipsData.answerRules?.[`sec${sectionIndex}_q${itemIndex}`]?.[String(answerValue)];

    (rule?.handouts || []).forEach((handoutId) => {
      const handout = tipsData.handouts?.[handoutId];
      if (!handout) {
        return;
      }

      if (!handouts.has(handoutId)) {
        handouts.set(handoutId, {
          id: handoutId,
          ...handout,
          triggers: [],
        });
      }

      handouts.get(handoutId).triggers.push({
        question,
        response: tipsData.responseValues?.[String(answerValue)] || `Response ${answerValue}`,
      });
    });
  });

  return Array.from(handouts.values());
};

const getDashboardBandRange = (range) => {
  const [startText, endText] = range.split('-');
  return {
    start: Number(startText),
    end: Number(endText),
  };
};

const formatDashboardDate = (dateValue) => {
  if (!dateValue) return '';
  return new Date(dateValue).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const HabcDashboardChart = ({ reports }) => {
  const sortedReports = [...reports].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  const chartReports = sortedReports.map((report) => ({
    report,
    score: parseReportScore(report, SECTIONS, 'habc'),
  }));
  // Which assessment's subscale scores are shown below the chart. Defaults
  // to the latest; tapping a point on the chart selects that assessment.
  const [selectedIndex, setSelectedIndex] = useState(null);
  const activeIndex = selectedIndex != null && selectedIndex < chartReports.length
    ? selectedIndex
    : chartReports.length - 1;
  const activeReport = chartReports[activeIndex]?.report;
  const width = 760;
  const height = 360;
  const margin = { top: 30, right: 30, bottom: 68, left: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const maxScore = HABC_TOTAL_MAX_SCORE;
  const getX = (index) => (
    chartReports.length <= 1
      ? margin.left + chartWidth / 2
      : margin.left + (index / (chartReports.length - 1)) * chartWidth
  );
  const getY = (score) => margin.top + (1 - Math.max(0, Math.min(maxScore, score)) / maxScore) * chartHeight;
  const linePath = chartReports
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${getX(index)} ${getY(point.score)}`)
    .join(' ');
  const activeSubscales = activeReport
    ? SECTIONS.map((section, sectionIndex) => {
      const score = (section.items || []).reduce((sum, _, itemIndex) =>
        sum + (activeReport.answers?.[`sec${sectionIndex}_q${itemIndex}`] || 0)
      , 0);
      return {
        title: section.title.replace(' Subscale', ''),
        score,
        maxScore: (section.items || []).length * 3,
        status: getHabcSubscaleStatus(sectionIndex, score),
      };
    })
    : [];

  return (
    <div className="mt-5 rounded-2xl border border-[#b9d8e8] bg-[#f7fbfe] p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[1.6rem] font-extrabold text-[#0f2b3a]">Dashboard</div>
          <div className="text-[1.15rem] font-semibold text-[#526b7a]">
            HABC total score trend over completed assessments.
          </div>
        </div>
        <span className="rounded-full border border-[#cfe2ee] bg-white px-4 py-1.5 text-[1.05rem] font-extrabold text-[#006298]">
          {chartReports.length} assessment{chartReports.length === 1 ? '' : 's'}
        </span>
      </div>

      {chartReports.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#b9d8e8] bg-white p-5 text-[1.15rem] font-semibold text-[#526b7a]">
          Complete a HABC assessment to build the dashboard trend.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-[#dceaf2] bg-white p-3">
            <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="HABC total score time series dashboard" className="min-w-[720px] w-full">
              {HABC_SCORE_BANDS.map((band) => {
                const { start, end } = getDashboardBandRange(band.range);
                const yTop = getY(end);
                const yBottom = getY(start);
                return (
                  <rect
                    key={band.label}
                    x={margin.left}
                    y={yTop}
                    width={chartWidth}
                    height={Math.max(1, yBottom - yTop)}
                    fill={getHabcGaugeArcColor(band.color)}
                    opacity="0.78"
                  />
                );
              })}
              {[0, 20, 40, 60, HABC_TOTAL_MAX_SCORE].map((tick) => (
                <g key={tick}>
                  <line x1={margin.left} y1={getY(tick)} x2={width - margin.right} y2={getY(tick)} stroke="#24465a" strokeOpacity="0.16" strokeWidth="1" />
                  <text x={margin.left - 12} y={getY(tick) + 6} textAnchor="end" className="fill-[#526b7a] text-[18px] font-extrabold">
                    {tick}
                  </text>
                </g>
              ))}
              <line x1={margin.left} y1={margin.top} x2={margin.left} y2={height - margin.bottom} stroke="#26343d" strokeWidth="2" />
              <line x1={margin.left} y1={height - margin.bottom} x2={width - margin.right} y2={height - margin.bottom} stroke="#26343d" strokeWidth="2" />
              {linePath && <path d={linePath} fill="none" stroke="#26343d" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />}
              {chartReports.map((point, index) => {
                const isActive = index === activeIndex;
                const x = getX(index);
                const y = getY(point.score);
                return (
                  <g
                    key={point.report.id || `${point.report.createdAt}-${index}`}
                    className="cursor-pointer outline-none focus:outline-none"
                    style={{ outline: 'none' }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Show scores for ${point.report.periodLabel || 'Report'}`}
                    onClick={() => setSelectedIndex(index)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedIndex(index);
                      }
                    }}
                  >
                    {/* generous invisible hit area so the point is easy to tap */}
                    <circle cx={x} cy={y} r="22" fill="transparent" />
                    {isActive && <circle cx={x} cy={y} r="16" fill="#006298" opacity="0.25" />}
                    <circle cx={x} cy={y} r={isActive ? 10 : 8} fill={isActive ? '#006298' : '#26343d'} stroke="#ffffff" strokeWidth="3" />
                    <text x={x} y={y - 18} textAnchor="middle" className={`${isActive ? 'fill-[#006298]' : 'fill-[#26343d]'} text-[20px] font-extrabold`}>
                      {point.score}
                    </text>
                    <text x={x} y={height - margin.bottom + 30} textAnchor="middle" className={`${isActive ? 'fill-[#006298]' : 'fill-[#17384a]'} text-[18px] font-extrabold`}>
                      {point.report.periodLabel || 'Report'}
                    </text>
                    <text x={x} y={height - margin.bottom + 52} textAnchor="middle" className="fill-[#526b7a] text-[15px] font-bold">
                      {formatDashboardDate(point.report.createdAt)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {activeSubscales.length > 0 && (
            <>
              <div className="mt-4 text-[1.15rem] font-semibold text-[#526b7a]">
                Showing <strong className="text-[#0f3950]">{activeReport.periodLabel || 'Report'}</strong>
                {' · '}
                {formatDashboardDate(activeReport.createdAt)}. Tap a point on the chart to see another assessment.
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {activeSubscales.map((subscale) => (
                  <div
                    key={subscale.title}
                    className={`rounded-xl border px-4 py-3 ${getHabcSubscaleCardClass(subscale.status.color)}`}
                  >
                    <div className="text-[1.05rem] font-extrabold uppercase tracking-wide text-[#526b7a]">{subscale.title}</div>
                    <div className="mt-2 flex flex-nowrap items-center justify-between gap-3">
                      <span className={`whitespace-nowrap rounded-full border px-3 py-1 text-[1rem] font-extrabold ${getHabcSubscaleBadgeClass(subscale.status.color)}`}>
                        {subscale.status.label}
                      </span>
                      <span className="shrink-0 whitespace-nowrap text-[1.35rem] font-extrabold text-[#0f3950]">{subscale.score}/{subscale.maxScore}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const getScheduleBadgeClass = ({ isComplete, dueNow }) => {
  if (isComplete) return 'bg-[#e5f6ea] text-[#1f7a3b] border border-[#b8e0c3]';
  if (dueNow) return 'bg-[#fde8e8] text-[#9b2424] border border-[#f3b8b8]';
  return 'bg-[#eef3f6] text-[#526b7a] border border-[#d7e3ea]';
};

const getPeriodCardClass = ({ completed, isNext, dueNow }) => {
  if (completed) return 'border-[#b8e0c3] bg-[#f1fbf4]';
  if (isNext && dueNow) return 'border-[#f3b8b8] bg-[#fff5f5]';
  return 'border-[#d7e3ea] bg-[#f7f9fb]';
};

const getQuestionnaireSections = (questionnaireId) => {
  if (questionnaireId === 'npi') return NPI_SECTIONS;
  if (questionnaireId === 'gad7') return GAD7_SECTIONS;
  if (questionnaireId === 'phq9') return PHQ9_SECTIONS;
  return SECTIONS;
};

const getQuestionnaireResponseOptions = (questionnaireId) => {
  if (questionnaireId === 'gad7') return GAD7_RESPONSE_OPTIONS;
  if (questionnaireId === 'phq9') return PHQ9_RESPONSE_OPTIONS;
  return HABC_RESPONSE_OPTIONS;
};

const getQuestionnaireMaxScore = (questionnaireId) => {
  if (questionnaireId === 'npi') return 144;
  if (questionnaireId === 'gad7') return 21;
  if (questionnaireId === 'phq9') return 27;
  return HABC_TOTAL_MAX_SCORE;
};

const hasQuestionnaireLanding = (questionnaireId) =>
  questionnaireId === 'habc' || questionnaireId === 'gad7' || questionnaireId === 'phq9';

const npiKey = (sectionIndex, field) => `npi${sectionIndex}_${field}`;
const npiFollowUpKey = (sectionIndex, itemIndex) => `npi${sectionIndex}_item${itemIndex}`;

const isNpiSectionScored = (section, answers, index) => {
  if (answers[npiKey(index, 'present')] !== 1) return false;
  if (section.convictionQuestion && answers[npiKey(index, 'convinced')] !== 1) return false;
  return true;
};

const isNpiSectionComplete = (section, answers, index) => {
  const present = answers[npiKey(index, 'present')];
  if (present == null) return false;
  if (present === 0) return true;

  if (section.convictionQuestion) {
    const convinced = answers[npiKey(index, 'convinced')];
    if (convinced == null) return false;
    if (convinced === 0) return true;
  }

  if ((section.followUpItems || []).some((_, itemIndex) => answers[npiFollowUpKey(index, itemIndex)] == null)) {
    return false;
  }

  return (
    answers[npiKey(index, 'frequency')] != null &&
    answers[npiKey(index, 'severity')] != null &&
    answers[npiKey(index, 'distress')] != null
  );
};

const getNpiSectionAnsweredCount = (section, answers, index) => {
  let answeredCount = 0;
  const present = answers[npiKey(index, 'present')];
  if (present == null) return answeredCount;
  answeredCount += 1;
  if (present === 0) return answeredCount;

  if (section.convictionQuestion) {
    const convinced = answers[npiKey(index, 'convinced')];
    if (convinced == null) return answeredCount;
    answeredCount += 1;
    if (convinced === 0) return answeredCount;
  }

  answeredCount += (section.followUpItems || []).filter((_, itemIndex) =>
    answers[npiFollowUpKey(index, itemIndex)] != null
  ).length;

  if (answers[npiKey(index, 'frequency')] != null) answeredCount += 1;
  if (answers[npiKey(index, 'severity')] != null) answeredCount += 1;
  if (answers[npiKey(index, 'distress')] != null) answeredCount += 1;

  return answeredCount;
};

const getNpiSectionExpectedCount = (section, answers, index) => {
  const present = answers[npiKey(index, 'present')];
  if (present === 0) return 1;

  const hasConvictionGate = Boolean(section.convictionQuestion);
  const convinced = answers[npiKey(index, 'convinced')];
  if (hasConvictionGate && convinced === 0) return 2;

  return 1 + (hasConvictionGate ? 1 : 0) + (section.followUpItems || []).length + 3;
};

export default function HabcMonitorForm({ open, onClose, userId, asPage = false }) {
  // load from localStorage or default to empty object
  const [answers, setAnswers] = useState({})
  const [reports, setReports] = useState([]);
  const [viewingReport, setViewingReport] = useState(null);
  const [visualizingReport, setVisualizingReport] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [habcTipsData, setHabcTipsData] = useState(null);
  const [habcTipsLoading, setHabcTipsLoading] = useState(false);
  const [habcTipsError, setHabcTipsError] = useState('');
  const [selectedTipSubscaleIndex, setSelectedTipSubscaleIndex] = useState(null);
  const [selectedTipHandoutId, setSelectedTipHandoutId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(DEFAULT_QUESTIONNAIRE_ID);
  const [showAssessmentLanding, setShowAssessmentLanding] = useState(false);
  const isVisible = asPage || open;
  // The app-wide avatar (mounted once in AppShell) reads questions aloud.
  // Auto-read waits for it to finish loading so the first question isn't
  // spoken by browser TTS while the avatar is still loading.
  const { avatarRef, avatarReady } = useAvatar();
  const qKey = (sectionIdx, itemIdx) => `sec${sectionIdx}_q${itemIdx}`
  // question-by-question navigation state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const activeQuestionnaireId = activeAssessment?.questionnaireId || 'habc';
  const activeQuestionnaire = QUESTIONNAIRES.find((item) => item.id === activeQuestionnaireId) || QUESTIONNAIRES[0];
  const activeSections = getQuestionnaireSections(activeQuestionnaireId);
  const responseOptions = getQuestionnaireResponseOptions(activeQuestionnaireId);
  const isNpiAssessment = activeQuestionnaireId === 'npi';

  // flatten all questions into a list
  const flatQuestions = activeSections.flatMap((sec, si) =>
    (sec.items || [sec.title]).map((item, qi) => ({
      sectionIdx: si,
      itemIdx: qi,
      text: typeof item === 'string' ? item : item.text,
      color: sec.color,
      prompt: sec.prompt,
    }))
  );

  const totalQuestions = flatQuestions.length;
  const {
    sectionIdx: curSecIdx,
    itemIdx: curItemIdx,
    text: curText,
    prompt: curPrompt,
  } = flatQuestions[currentQuestion];
  const curKey = qKey(curSecIdx, curItemIdx);

  const pickMatchingVoice = (voices, speechLang, gender) => {
    const normalizedLang = String(speechLang || '').toLowerCase();
    const languageFamily = normalizedLang.split('-')[0];

    const genderHints =
      gender === 'male'
        ? ['male', 'david', 'daniel', 'alex', 'george', 'james', 'lee']
        : ['female', 'zira', 'samantha', 'victoria', 'karen', 'ava', 'aria'];

    const sameLanguageVoices = voices.filter(
      (voice) => String(voice.lang || '').toLowerCase() === normalizedLang
    );

    const familyVoices = voices.filter((voice) =>
      String(voice.lang || '').toLowerCase().startsWith(`${languageFamily}-`)
    );

    const candidateVoices = sameLanguageVoices.length ? sameLanguageVoices : familyVoices;

    return (
      candidateVoices.find((voice) =>
        genderHints.some((hint) => String(voice.name || '').toLowerCase().includes(hint))
      ) ||
      candidateVoices[0] ||
      voices[0] ||
      null
    );
  };

  // `silent` suppresses the unsupported-browser alert (used by auto-read).
  const readCurrentQuestion = ({ silent = false } = {}) => {
    const voiceConfig = getTalkingHeadVoiceConfig();

    const textToRead = `${curText} ${HABC_RESPONSE_READOUT}`;

    // Preferred path: let the 3D avatar speak it (with lip-sync) when present.
    if (avatarRef.current && typeof avatarRef.current.speakText === 'function') {
      try {
        avatarRef.current.speakText(textToRead);
        return;
      } catch (err) {
        console.warn('Avatar speech failed, falling back to browser TTS.', err);
      }
    }

    // Fallback path: browser speech synthesis (e.g. avatar not mounted / WebGL off).
    if (
      typeof window === 'undefined' ||
      !window.speechSynthesis ||
      typeof window.SpeechSynthesisUtterance !== 'function'
    ) {
      if (!silent) {
        alert('Speech synthesis is not supported in this browser.');
      }
      return;
    }

    const utterance = new window.SpeechSynthesisUtterance(textToRead);
    utterance.lang = voiceConfig.speechLang;
    utterance.rate = voiceConfig.rate || 0.9;
    utterance.pitch = voiceConfig.pitch || 1;
    utterance.volume = 1;

    const voices = window.speechSynthesis.getVoices?.() || [];
    const selectedVoice = pickMatchingVoice(
      voices,
      voiceConfig.speechLang,
      voiceConfig.gender
    );

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  // Read each question aloud as it appears in the standard question view
  // (not on the report list, landing page, or NPI section view).
  const questionViewActive =
    isVisible && !!activeAssessment && !showAssessmentLanding && !isNpiAssessment;
  const stopReading = () => {
    if (avatarRef.current && typeof avatarRef.current.stopSpeaking === 'function') {
      avatarRef.current.stopSpeaking();
    } else if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  // Cut off any speech in progress, pause briefly, then read the new question.
  useEffect(() => {
    if (!questionViewActive || !avatarReady) {
      return undefined;
    }
    stopReading();
    const timer = setTimeout(() => readCurrentQuestion({ silent: true }), QUESTION_READ_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionViewActive, avatarReady, currentQuestion]);

  // Leaving the question view should not leave a question playing.
  useEffect(() => {
    if (!questionViewActive) {
      stopReading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionViewActive]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const resolvedUserId = userId || localStorage.getItem('userId') || '';
    if (!resolvedUserId) {
      setLoadError('Unable to load monitor data yet. Please wait a moment and reopen.');
      setReports([]);
      return;
    }

    let canceled = false;
    setLoading(true);
    setLoadError('');

    axios
      .get(`${config.API_BASE_URL}/get-all-reports`, { params: { userId: resolvedUserId } })
      .then((reportsResponse) => {
        if (canceled) {
          return;
        }

        setReports(reportsResponse?.data?.reports || []);
      })
      .catch((err) => {
        if (canceled) {
          return;
        }
        console.error('Failed to load monitor data:', err);
        setLoadError('Failed to load monitor data. Please try again.');
        setReports([]);
      })
      .finally(() => {
        if (!canceled) {
          setLoading(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [isVisible, userId])

  useEffect(() => {
    if (!isVisible || habcTipsData || habcTipsLoading) {
      return;
    }

    let canceled = false;
    setHabcTipsLoading(true);
    setHabcTipsError('');

    axios
      .get(`${config.API_BASE_URL}/habc-tips`)
      .then((tipsResponse) => {
        if (!canceled) {
          setHabcTipsData(tipsResponse?.data || null);
        }
      })
      .catch((err) => {
        if (!canceled) {
          console.error('Failed to load HABC handout tips:', err);
          setHabcTipsError('Unable to load handout tips right now.');
        }
      })
      .finally(() => {
        if (!canceled) {
          setHabcTipsLoading(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [isVisible, habcTipsData, habcTipsLoading]);

  const sharedDialogProps = {
    open: isVisible,
    onClose,
    fullWidth: true,
    maxWidth: 'lg',
  };

  const returnToQuestionnaires = () => {
    setViewingReport(null);
    setVisualizingReport(null);
    setShowDashboard(false);
    setSelectedTipSubscaleIndex(null);
    setSelectedTipHandoutId(null);
    setShowAssessmentLanding(false);
    setActiveAssessment(null);
    setSelectedQuestionnaireId(DEFAULT_QUESTIONNAIRE_ID);
    setAnswers({});
    setCurrentQuestion(0);
  };

  const handleShellBack = () => {
    if (showDashboard) {
      setShowDashboard(false);
      return;
    }

    if (selectedTipSubscaleIndex != null) {
      if (selectedTipHandoutId) {
        setSelectedTipHandoutId(null);
        return;
      }
      setSelectedTipSubscaleIndex(null);
      return;
    }

    if (viewingReport) {
      setViewingReport(null);
      return;
    }

    if (visualizingReport) {
      setVisualizingReport(null);
      return;
    }

    if (activeAssessment) {
      // Back from an in-progress questionnaire returns to its detail view (page 2).
      returnToQuestionnaires();
      return;
    }

    // The questionnaire list page is removed, so Back from the detail view exits home.
    onClose();
  };

  // `hideHeader`: for views that render their own title + Back button, so the
  // caregiver doesn't see two headers stacked.
  const renderPageShell = (content, { hideHeader = false } = {}) => {
    if (!asPage) {
      return <Dialog {...sharedDialogProps}>{content}</Dialog>;
    }

    return (
      <div className="h-full overflow-auto p-4 md:p-7">
        <div className="mx-auto w-full lg:w-[92%] xl:w-[90%] max-w-[1300px]">
          <div className="vica-surface p-5 md:p-7">
            {!hideHeader && (
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="m-0 text-[2rem] font-extrabold text-[#0f2b3a]">HABC Monitor</h2>
              <button type="button" className="vica-btn-secondary vica-session-btn-new" onClick={handleShellBack}>
                <ArrowBackIcon sx={{ fontSize: 28 }} />
                Back
              </button>
            </div>
            )}
            <div>
              {content}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Layout for the question-answering view (full-bleed):
  //   - LEFT : the 3D avatar, large, anchored to the bottom (like the chat page)
  //   - RIGHT: a bigger card that frames ONLY the question, floating over the avatar
  const renderFormShell = (content) => {
    if (!asPage) {
      // Dialog/modal mode keeps the original compact layout.
      return <Dialog {...sharedDialogProps}>{content}</Dialog>;
    }

    // Page mode: the app shell already shows the avatar beside this column,
    // so the question card simply fills the column.
    return (
      <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-white/30 bg-white/95 shadow-2xl backdrop-blur">
        <div className="flex items-center justify-between gap-3 border-b border-[#e3eef5] px-5 py-4">
          <h2 className="m-0 text-[2rem] font-extrabold text-[#0f2b3a]">HABC Monitor</h2>
          <button
            type="button"
            className="vica-btn-secondary vica-session-btn-new"
            onClick={handleShellBack}
          >
            <ArrowBackIcon sx={{ fontSize: 28 }} />
            Back
          </button>
        </div>
        <div className="overflow-auto">{content}</div>
      </div>
    );
  };

  const assessmentSchedules = useMemo(
    () => QUESTIONNAIRES.map((questionnaire) => getQuestionnaireSchedule(questionnaire, reports)),
    [reports]
  );

  const habcSchedule = assessmentSchedules.find((schedule) => schedule.questionnaire.id === 'habc');

  const handleChange = (key) => (e) => {
    const val = parseInt(e.target.value, 10)
    const next = { ...answers, [key]: val }
    setAnswers(next)
  }

  const setAnswerValue = (key, value) => {
    setAnswers((current) => {
      const next = { ...current, [key]: value };
      if (key.endsWith('_present') && value === 0) {
        const prefix = key.replace('_present', '');
        delete next[`${prefix}_convinced`];
        delete next[`${prefix}_frequency`];
        delete next[`${prefix}_severity`];
        delete next[`${prefix}_distress`];
        Object.keys(next).forEach((answerKey) => {
          if (answerKey.startsWith(`${prefix}_item`)) {
            delete next[answerKey];
          }
        });
      }
      if (key.endsWith('_convinced') && value === 0) {
        const prefix = key.replace('_convinced', '');
        delete next[`${prefix}_frequency`];
        delete next[`${prefix}_severity`];
        delete next[`${prefix}_distress`];
        Object.keys(next).forEach((answerKey) => {
          if (answerKey.startsWith(`${prefix}_item`)) {
            delete next[answerKey];
          }
        });
      }
      return next;
    });
  };

  // helper to build a unique key for each question


  // calculate totals per section
  const currentAnswers = viewingReport ? viewingReport.answers : answers;

  const sectionTotals = activeSections.map((sec, si) =>
    (sec.items || []).reduce((sum, _, qi) => sum + (currentAnswers[qKey(si, qi)] || 0), 0)
  )

  const totalScore = useMemo(
    () => sectionTotals.reduce((sum, val) => sum + val, 0),
    [sectionTotals]
  )
  const answeredQuestionsCount = flatQuestions.filter((question) =>
    answers[qKey(question.sectionIdx, question.itemIdx)] != null
  ).length;
  const standardProgressPercent = totalQuestions
    ? Math.round((answeredQuestionsCount / totalQuestions) * 100)
    : 0;
  const npiAnsweredCount = isNpiAssessment
    ? NPI_SECTIONS.reduce((sum, section, index) =>
      sum + getNpiSectionAnsweredCount(section, answers, index)
    , 0)
    : 0;
  const npiExpectedCount = isNpiAssessment
    ? NPI_SECTIONS.reduce((sum, section, index) =>
      sum + getNpiSectionExpectedCount(section, answers, index)
    , 0)
    : 0;
  const npiProgressPercent = npiExpectedCount
    ? Math.round((npiAnsweredCount / npiExpectedCount) * 100)
    : 0;
  const progressPercent = isNpiAssessment ? npiProgressPercent : standardProgressPercent;
  const currentNpiSection = isNpiAssessment ? NPI_SECTIONS[currentQuestion] : null;

  const handleSubmit = async () => {
    // Check if all questions are answered before submitting
    if (activeQuestionnaireId === 'npi') {
      const incompleteSection = NPI_SECTIONS.find((section, index) =>
        !isNpiSectionComplete(section, answers, index)
      );

      if (incompleteSection) {
        alert('Please complete each NPI section before submitting.');
        return;
      }
    } else {
      const answeredCount = Object.keys(answers).length;
      if (answeredCount < totalQuestions) {
        alert('Please answer all questions before submitting.');
        return;
      }
    }
    const resolvedUserId = userId || localStorage.getItem('userId') || '';
    if (!resolvedUserId) {
      alert('Unable to submit right now. Please reopen monitor and try again.');
      return;
    }
    const report = {
      userId: resolvedUserId,
      questionnaireType: activeQuestionnaireId,
      periodKey: activeAssessment?.periodKey || habcSchedule?.nextPeriod?.key || 'baseline',
      periodLabel: activeAssessment?.periodLabel || habcSchedule?.nextPeriod?.label || 'Baseline',
      answers,
    };

    if (activeQuestionnaireId === 'npi') {
      const npiTotal = NPI_SECTIONS.reduce((sum, section, index) => {
        if (!isNpiSectionScored(section, answers, index)) return sum;
        return sum + ((answers[npiKey(index, 'frequency')] || 0) * (answers[npiKey(index, 'severity')] || 0));
      }, 0);
      const distressTotal = NPI_SECTIONS.reduce((sum, section, index) => {
        if (!isNpiSectionScored(section, answers, index)) return sum;
        return sum + (answers[npiKey(index, 'distress')] || 0);
      }, 0);
      report.total = `${npiTotal}/144`;
      report.distressTotal = `${distressTotal}/60`;
    } else if (activeQuestionnaireId === 'gad7') {
      report.total = `${totalScore}/21`;
      report.severity = getGad7Severity(totalScore);
    } else if (activeQuestionnaireId === 'phq9') {
      report.total = `${totalScore}/27`;
    } else {
      report.cognitive = `${sectionTotals[0]}/18`;
      report.functional = `${sectionTotals[1]}/33`;
      report.mood = `${sectionTotals[2]}/30`;
      report.stress = `${sectionTotals[3]}/12`;
      report.total = `${totalScore}/${HABC_TOTAL_MAX_SCORE}`;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await axios.post(`${config.API_BASE_URL}/submit-biweekly-monitor`, report);

      if (activeQuestionnaireId !== 'habc') {
        window.location.reload();
        return;
      }

      // Go straight to the results: build the saved report locally (same shape
      // /get-all-reports returns), add it to the list, leave the form, and open
      // its visualization instead of making the caregiver click "Visualize".
      const savedReport = {
        ...report,
        id: response?.data?.id || `local-${Date.now()}`,
        createdAt: new Date().toISOString(),
      };
      setReports((prev) => [savedReport, ...prev]);
      returnToQuestionnaires();
      setVisualizingReport(savedReport);
    } catch (err) {
      console.error('Failed to submit biweekly monitor:', err);
      setSubmitError('We could not submit your answers. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (visualizingReport) {
    const visualScore = parseReportScore(visualizingReport, SECTIONS, 'habc');
    const visualDate = visualizingReport.createdAt
      ? new Date(visualizingReport.createdAt).toLocaleDateString()
      : '';
    const selectedTipSection = selectedTipSubscaleIndex != null
      ? SECTIONS[selectedTipSubscaleIndex]
      : null;
    const selectedTipHandouts = selectedTipSubscaleIndex != null
      ? getHabcSubscaleHandoutTips(habcTipsData, visualizingReport, selectedTipSubscaleIndex)
      : [];
    const selectedTipHandout = selectedTipHandouts.find((handout) => handout.id === selectedTipHandoutId) || null;
    const visualContent = (
      <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
        <div className="mx-auto w-full max-w-6xl rounded-3xl border border-[#b9d8e8] bg-white/92 p-4 shadow-sm sm:p-6">
          <div className="mb-5 flex flex-nowrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="m-0 text-[2rem] font-extrabold text-[#0f2b3a]">
                HABC Visualization · {visualizingReport.periodLabel || 'Report'}
              </h2>
              <p className="vica-subtitle !text-[1.25rem]">
                {visualDate && <>{visualDate} · </>}
                Total score interpreted using HABC status bands.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedTipSubscaleIndex(null);
                setSelectedTipHandoutId(null);
                setVisualizingReport(null);
              }}
              className="vica-btn-secondary vica-session-btn-new shrink-0"
            >
              <ArrowBackIcon sx={{ fontSize: 28 }} />
              Back
            </button>
          </div>

          <div className="mx-auto max-w-xl">
            <HabcScoreGauge score={visualScore} />
          </div>

          <div className="mt-4 rounded-2xl border border-[#d7e7f0] bg-[#f7fbfe] p-4">
            <div className="mb-4">
              <div className="text-[1.6rem] font-extrabold text-[#0f3950]">Subscale Scores</div>
              <div className="text-[1.15rem] font-semibold text-[#526b7a]">
                Breakdown by HABC domain from this saved report.
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {SECTIONS.map((section, sectionIndex) => {
                const sectionTotal = (section.items || []).reduce((sum, _, itemIndex) =>
                  sum + (visualizingReport.answers?.[qKey(sectionIndex, itemIndex)] || 0)
                , 0);

                return (
                  <HabcSubscaleGauge
                    key={section.title}
                    title={section.title}
                    score={sectionTotal}
                    sectionIndex={sectionIndex}
                    onTipsClick={setSelectedTipSubscaleIndex}
                    tipsDisabled={habcTipsLoading}
                  />
                );
              })}
            </div>
          </div>

          <Dialog
            open={selectedTipSubscaleIndex != null}
            onClose={() => {
              setSelectedTipSubscaleIndex(null);
              setSelectedTipHandoutId(null);
            }}
            fullWidth
            maxWidth="lg"
            PaperProps={{
              sx: {
                borderRadius: { xs: '24px', sm: '32px' },
                overflow: 'hidden',
                m: { xs: 2, sm: 3 },
              },
            }}
          >
            <div className="max-h-[calc(100dvh-2rem)] overflow-y-auto bg-gradient-to-b from-[#edf7ff] via-white to-[#f7fbfe] p-4 sm:max-h-[calc(100dvh-3rem)] sm:p-6">
              <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#006298] to-[#0f3950] p-4 text-white shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#bde7ff]">
                      Recommended Caregiver Handouts
                    </div>
                    <h2 className="m-0 mt-1 text-2xl font-black">Handout Tips</h2>
                    <p className="m-0 mt-1 text-sm font-semibold text-[#d8f1ff]">
                      {selectedTipSection?.title || 'HABC'} recommendations based on this report's answers.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-full border border-white/40 bg-white/15 px-4 py-2 text-sm font-extrabold text-white transition hover:bg-white/25"
                    onClick={() => {
                      setSelectedTipSubscaleIndex(null);
                      setSelectedTipHandoutId(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {selectedTipHandout ? (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#cfe2ee] bg-white px-4 py-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-[#b9d8e8] bg-[#edf7ff] px-3 py-2 text-sm font-extrabold text-[#006298] transition hover:bg-[#dff0fc]"
                    onClick={() => setSelectedTipHandoutId(null)}
                  >
                    <span className="text-lg leading-none">&lt;</span>
                    Back to handouts
                  </button>
                  <span className="rounded-full border border-[#b9d8e8] bg-white px-3 py-1 text-xs font-extrabold text-[#006298]">
                    Handout #{selectedTipHandout.number}
                  </span>
                </div>
              ) : (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#cfe2ee] bg-white px-4 py-3">
                  <div>
                    <div className="text-xs font-extrabold uppercase tracking-wide text-[#526b7a]">Subscale</div>
                    <div className="text-base font-extrabold text-[#0f3950]">{selectedTipSection?.title || 'HABC'}</div>
                  </div>
                  <span className="rounded-full border border-[#b9d8e8] bg-[#edf7ff] px-3 py-1 text-xs font-extrabold text-[#006298]">
                    {selectedTipHandouts.length} handout{selectedTipHandouts.length === 1 ? '' : 's'}
                  </span>
                </div>
              )}

              {habcTipsLoading && (
                <div className="rounded-xl border border-[#dceaf2] bg-white p-4 text-sm font-semibold text-[#526b7a]">
                  Loading handout tips...
                </div>
              )}

              {!habcTipsLoading && habcTipsError && (
                <div className="rounded-xl border border-[#f3b8b8] bg-[#fff5f5] p-4 text-sm font-semibold text-[#9b2424]">
                  {habcTipsError}
                </div>
              )}

              {!habcTipsLoading && !habcTipsError && selectedTipHandouts.length === 0 && (
                <div className="rounded-xl border border-[#dceaf2] bg-white p-4 text-sm font-semibold text-[#526b7a]">
                  No handout tips were triggered for this subscale based on the saved answers.
                </div>
              )}

              {!habcTipsLoading && !habcTipsError && selectedTipHandouts.length > 0 && !selectedTipHandout && (
                <div className="space-y-4">
                  {selectedTipHandouts.map((handout) => (
                    <button
                      key={handout.id}
                      type="button"
                      onClick={() => setSelectedTipHandoutId(handout.id)}
                      className="w-full overflow-hidden rounded-3xl border border-[#cfe2ee] bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#006298] hover:shadow-md"
                    >
                      <div className="flex items-stretch">
                        <div className="flex w-16 shrink-0 items-center justify-center bg-gradient-to-b from-[#006298] to-[#0f3950] text-lg font-black text-white">
                          #{handout.number}
                        </div>
                        <div className="min-w-0 flex-1 p-4">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-lg font-black text-[#0f3950]">{handout.title}</div>
                              {handout.category && (
                                <div className="mt-1 text-xs font-extrabold uppercase tracking-wide text-[#526b7a]">
                                  {handout.category}
                                </div>
                              )}
                            </div>
                            <span className="rounded-full border border-[#b8e0c3] bg-[#e5f6ea] px-3 py-1 text-xs font-extrabold text-[#1f7a3b]">
                              {handout.triggers.length} trigger{handout.triggers.length === 1 ? '' : 's'}
                            </span>
                          </div>
                          <div className="mt-3 rounded-xl border border-[#dceaf2] bg-[#f7fbfe] px-3 py-2 text-sm text-[#17384a]">
                            <span className="mr-2 rounded-full bg-[#e7f3fb] px-2 py-0.5 text-xs font-extrabold text-[#006298]">
                              {handout.triggers[0]?.response}
                            </span>
                            {handout.triggers[0]?.question}
                          </div>
                        </div>
                        <div className="flex w-12 shrink-0 items-center justify-center bg-[#f7fbfe] text-2xl font-black text-[#006298]">
                          &gt;
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!habcTipsLoading && !habcTipsError && selectedTipHandout && (
                <div className="overflow-hidden rounded-3xl border border-[#cfe2ee] bg-white shadow-sm">
                  <div className="border-b border-[#dceaf2] bg-gradient-to-r from-[#e7f3fb] to-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#006298] text-lg font-black text-white shadow-sm">
                          #{selectedTipHandout.number}
                        </div>
                        <div>
                          <div className="text-xl font-black text-[#0f3950]">{selectedTipHandout.title}</div>
                          <div className="mt-1 inline-flex rounded-full border border-[#cfe2ee] bg-white px-3 py-1 text-xs font-extrabold text-[#526b7a]">
                            {selectedTipHandout.category || `Handout #${selectedTipHandout.number}`}
                          </div>
                        </div>
                      </div>
                      <span className="rounded-full border border-[#b8e0c3] bg-[#e5f6ea] px-3 py-1 text-xs font-extrabold text-[#1f7a3b]">
                        {selectedTipHandout.triggers.length} trigger{selectedTipHandout.triggers.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="rounded-2xl border border-[#b9d8e8] bg-[#f2f9fd] p-3">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#006298]" />
                        <div className="text-xs font-extrabold uppercase tracking-wide text-[#0f3950]">
                          Why this appears
                        </div>
                      </div>
                      {selectedTipHandout.triggers.map((trigger, triggerIndex) => (
                        <div
                          key={`${selectedTipHandout.id}-${triggerIndex}`}
                          className="mb-2 last:mb-0 rounded-xl border border-[#dceaf2] bg-white px-3 py-2 text-sm text-[#17384a]"
                        >
                          <span className="mr-2 rounded-full bg-[#e7f3fb] px-2 py-0.5 text-xs font-extrabold text-[#006298]">
                            {trigger.response}
                          </span>
                          {trigger.question}
                        </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <div className="rounded-2xl border border-[#edf3f7] bg-[#fffdf8] p-4">
                        <div className="whitespace-pre-wrap text-sm leading-6 text-[#526b7a]">
                          {selectedTipHandout.content || ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Dialog>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {HABC_SCORE_BANDS.map((band) => (
              <div key={band.label} className={`rounded-xl border px-3 py-3 text-center text-[1.1rem] font-extrabold ${getHabcStatusColor(band.label)}`}>
                <div>{band.range}</div>
                <div>{band.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    return renderPageShell(visualContent, { hideHeader: true });
  }

  // Display a selected past report
  if (viewingReport) {
    const viewingQuestionnaireId = getReportQuestionnaireId(viewingReport);
    const viewingSections = getQuestionnaireSections(viewingQuestionnaireId);
    const viewingOptions = getQuestionnaireResponseOptions(viewingQuestionnaireId);
    const viewingTotal = viewingSections.reduce((sum, sec, si) =>
      sum + (sec.items || []).reduce((sectionSum, _, qi) => sectionSum + (viewingReport.answers[qKey(si, qi)] || 0), 0)
    , 0);

    if (viewingQuestionnaireId === 'npi') {
      const npiViewingContent = (
        <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
          <div className="mb-4">
            <h2 className="m-0 text-xl font-extrabold text-[#0f2b3a]">
              NPI · {viewingReport.periodLabel || 'Report'}
            </h2>
            <p className="vica-subtitle">
              Total score: <strong>{viewingReport.total || '0/144'}</strong>
              {viewingReport.distressTotal && <> · Distress: <strong>{viewingReport.distressTotal}</strong></>}
            </p>
          </div>
          <div className="space-y-3">
            {NPI_SECTIONS.map((section, index) => {
              const present = viewingReport.answers?.[npiKey(index, 'present')];
              const convinced = viewingReport.answers?.[npiKey(index, 'convinced')];
              const frequency = viewingReport.answers?.[npiKey(index, 'frequency')];
              const severity = viewingReport.answers?.[npiKey(index, 'severity')];
              const distress = viewingReport.answers?.[npiKey(index, 'distress')];
              const isScored = isNpiSectionScored(section, viewingReport.answers || {}, index);

              return (
                <div key={section.title} className="rounded-lg border border-[#dceaf2] bg-white px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-extrabold text-[#0f3950]">{section.title}</div>
                    <span className={`rounded-full border px-3 py-1 text-xs font-extrabold ${
                      present === 1
                        ? 'bg-[#fde8e8] text-[#9b2424] border-[#f3b8b8]'
                        : 'bg-[#e5f6ea] text-[#1f7a3b] border-[#b8e0c3]'
                    }`}>
                      {present === 1 ? 'Present' : 'Not present'}
                    </span>
                  </div>
                  {present === 1 && (
                    <>
                      {section.convictionQuestion && (
                        <div className="mt-2 text-sm text-[#526b7a]">
                          Convinced: <strong>{convinced === 1 ? 'Yes' : convinced === 0 ? 'No' : '-'}</strong>
                        </div>
                      )}
                      {(section.followUpItems || []).length > 0 && convinced === 1 && (
                        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-2 text-sm text-[#526b7a]">
                          {section.followUpItems.map((item, itemIndex) => {
                            const answer = viewingReport.answers?.[npiFollowUpKey(index, itemIndex)];
                            return (
                              <div key={item} className="rounded-md border border-[#edf3f7] bg-[#fbfdff] px-3 py-2">
                                <span>{item}</span>
                                <strong className="ml-2 text-[#0f3950]">{answer === 1 ? 'Yes' : answer === 0 ? 'No' : '-'}</strong>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {isScored && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-[#526b7a]">
                          <div>Frequency: <strong>{frequency || '-'}</strong></div>
                          <div>Severity: <strong>{severity || '-'}</strong></div>
                          <div>Distress: <strong>{distress ?? '-'}</strong></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="p-4 text-right">
            <button onClick={() => setViewingReport(null)} className="vica-btn-secondary vica-btn-md">
              Close
            </button>
          </div>
        </div>
      );

      return asPage
        ? renderPageShell(npiViewingContent)
        : (
          <Dialog open onClose={() => setViewingReport(null)} fullWidth maxWidth="lg">
            {npiViewingContent}
          </Dialog>
        );
    }

    if (viewingQuestionnaireId === 'gad7' || viewingQuestionnaireId === 'phq9') {
      const viewingQuestionnaire = QUESTIONNAIRES.find((item) => item.id === viewingQuestionnaireId);
      const maxScore = getQuestionnaireMaxScore(viewingQuestionnaireId);
      const scoreDescription = viewingQuestionnaireId === 'gad7'
        ? ` · ${viewingReport.severity || getGad7Severity(viewingTotal)}`
        : '';
      const scaleViewingContent = (
        <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
          <div className="mb-4">
            <h2 className="m-0 text-xl font-extrabold text-[#0f2b3a]">
              {viewingQuestionnaire?.title || 'Questionnaire'} · {viewingReport.periodLabel || 'Report'}
            </h2>
            <p className="vica-subtitle">
              Total score: <strong>{viewingTotal}/{maxScore}</strong>
              {viewingQuestionnaireId === 'gad7' && (
                <span
                  className={`ml-2 inline-flex rounded-full border px-2.5 py-1 text-xs font-extrabold ${getGad7SeverityColor(viewingReport.severity || viewingTotal)}`}
                >
                  {viewingReport.severity || getGad7Severity(viewingTotal)}
                </span>
              )}
              {viewingQuestionnaireId !== 'gad7' && scoreDescription}
            </p>
          </div>
          <table className="min-w-full border-collapse text-sm" style={{ border: '1px solid #3f6f89' }}>
            <thead>
              <tr className="bg-[#e7f3fb]">
                <th className="px-2 py-2 border text-left">Previously answered questions</th>
                {viewingOptions.map((option) => (
                  <th key={option.value} className="px-2 py-2 border">{option.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viewingSections[0].items.map((text, qi) => {
                const key = qKey(0, qi);
                const ans = viewingReport.answers[key];
                return (
                  <tr key={key}>
                    <td className="px-2 py-2 border">{text}</td>
                    {viewingOptions.map((option) => (
                      <td key={option.value} className="text-center border">
                        <input
                          type="checkbox"
                          disabled
                          checked={ans === option.value}
                          style={{ width: '18px', height: '18px' }}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="p-4 text-right">
            <button onClick={() => setViewingReport(null)} className="vica-btn-secondary vica-btn-md">
              Close
            </button>
          </div>
        </div>
      );

      return asPage
        ? renderPageShell(scaleViewingContent)
        : (
          <Dialog open onClose={() => setViewingReport(null)} fullWidth maxWidth="lg">
            {scaleViewingContent}
          </Dialog>
        );
    }

    const habcStatus = getHabcStatus(viewingTotal);
    const habcStatusDescription = getHabcStatusDescription(viewingTotal);
    const habcStatusColor = getHabcGaugeColor(viewingTotal);
    const habcStatusTextColor = getHabcGaugeTextColor(viewingTotal);

    const viewingContent = (
      <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
        <div className="mx-auto w-full max-w-6xl rounded-3xl border border-[#b9d8e8] bg-white/92 p-4 shadow-sm sm:p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="m-0 text-2xl font-extrabold text-[#0f2b3a]">
                HABC Report · {viewingReport.periodLabel || 'Report'}
              </h2>
              <p className="vica-subtitle">
                Previously answered questions and saved score summary.
              </p>
            </div>
            <span className={`rounded-full border px-3 py-1 text-xs font-extrabold ${getHabcStatusColor(habcStatus)}`}>
              {habcStatus} · {viewingTotal}/{HABC_TOTAL_MAX_SCORE}
            </span>
          </div>

          <div className="overflow-hidden rounded-2xl border border-[#cfe2ee] bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-[860px] w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#e7f3fb] text-[#0f3950]">
                    <th className="w-[46%] px-4 py-3 text-left font-extrabold">
                      Previously answered questions
                    </th>
                    {HABC_RESPONSE_OPTIONS.map((option) => (
                      <th key={option.value} className="px-3 py-3 text-center font-extrabold">
                        {option.label.replace(` (${option.value})`, '')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SECTIONS.map((sec, si) =>
                    sec.items.map((text, qi) => {
                      const key = qKey(si, qi);
                      const ans = viewingReport.answers?.[key];
                      return (
                        <tr key={key} className="border-t border-[#dceaf2]" style={{ backgroundColor: sec.color }}>
                          <td className="px-4 py-3 font-semibold text-[#17384a]">
                            <div className="text-[11px] font-extrabold uppercase tracking-wide text-[#526b7a]">
                              {sec.title}
                            </div>
                            <div>{text}</div>
                          </td>
                          {[0, 1, 2, 3].map((val) => {
                            const selected = ans === val;
                            return (
                              <td key={val} className="px-3 py-3 text-center">
                                <span
                                  className={`inline-flex min-h-9 min-w-9 items-center justify-center rounded-full border text-xs font-extrabold transition ${
                                    selected
                                      ? HABC_SELECTED_RESPONSE_CLASSES[val]
                                      : 'border-[#cfe2ee] bg-white/70 text-transparent'
                                  }`}
                                >
                                  {selected ? 'Selected' : '-'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,0.35fr)_minmax(0,0.65fr)]">
            <div
              className="rounded-2xl p-5 shadow-sm"
              style={{ backgroundColor: habcStatusColor, color: habcStatusTextColor }}
            >
              <div className="text-sm font-extrabold uppercase tracking-wide opacity-90">Total Status</div>
              <div className="mt-2 text-2xl font-black">{habcStatusDescription}</div>
              <div className="mt-1 text-sm font-extrabold opacity-95">
                {habcStatus} · {viewingTotal}/{HABC_TOTAL_MAX_SCORE}
              </div>
            </div>
            <div className="rounded-2xl border border-[#cfe2ee] bg-[#f7fbfe] p-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {SECTIONS.map((sec, si) => {
                  const sectionTotal = sec.items.reduce((sum, _, qi) =>
                    sum + (viewingReport.answers?.[qKey(si, qi)] || 0)
                  , 0);
                  const sectionMax = sec.items.length * 3;
                  const subscaleStatus = getHabcSubscaleStatus(si, sectionTotal);

                  return (
                    <div
                      key={sec.title}
                      className={`rounded-xl border px-3 py-2 ${getHabcSubscaleCardClass(subscaleStatus.color)}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-extrabold uppercase tracking-wide text-[#526b7a]">
                          {sec.title}
                        </span>
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-extrabold ${getHabcSubscaleBadgeClass(subscaleStatus.color)}`}>
                          {subscaleStatus.label} · {sectionTotal}/{sectionMax}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div className="rounded-xl border border-[#b9d8e8] bg-[#e7f3fb] px-3 py-2 sm:col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-extrabold uppercase tracking-wide text-[#0f3950]">
                      Total Score
                    </span>
                    <span className="text-sm font-extrabold text-[#0f3950]">{viewingTotal}/{HABC_TOTAL_MAX_SCORE}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 text-right">
            <button onClick={() => setViewingReport(null)} className="vica-btn-secondary vica-btn-md">
              Close
            </button>
          </div>
        </div>
      </div>
    );

    if (asPage) {
      return renderPageShell(viewingContent);
    }

    return (
      <Dialog
        open
        onClose={() => setViewingReport(null)}
        fullWidth
        maxWidth="lg"
      >
        {viewingContent}
      </Dialog>
    );
  }
  if (loading) {
    return renderPageShell(
      <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
        Loading monitor data...
      </div>
    );
  }

  if (loadError) {
    return renderPageShell(
      <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
        <strong>{loadError}</strong>
      </div>
    );
  }

  if (!activeAssessment) {
    const selectedSchedule = assessmentSchedules.find(
      (schedule) => schedule.questionnaire.id === selectedQuestionnaireId
    );

    if (selectedSchedule) {
      const { questionnaire, nextPeriod, dueNow, statusText } = selectedSchedule;
      const isComplete = !nextPeriod;
      const isAvailableNow = questionnaire.available && nextPeriod && dueNow;
      const latestReport = selectedSchedule.reports[selectedSchedule.reports.length - 1];
      const latestStatus = latestReport ? getReportStatus(questionnaire.id, latestReport) : null;
      const detailContent = (
        <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
          <div className="rounded-xl border border-[#cfe2ee] bg-white shadow-sm p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-[2.2rem] font-extrabold text-[#0f2b3a]">{questionnaire.title}</h2>
                <p className="m-0 mt-1 text-[1.25rem] text-[#526b7a]">{questionnaire.description}</p>
                {latestStatus && (questionnaire.id === 'habc' || questionnaire.id === 'gad7') && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-[1.05rem] font-extrabold uppercase tracking-wide text-[#526b7a]">
                      Latest status
                    </span>
                    <span className={`rounded-full border px-4 py-1.5 text-[1.1rem] font-extrabold ${latestStatus.colorClass}`}>
                      {latestStatus.label} · {latestStatus.score}/{getQuestionnaireMaxScore(questionnaire.id)}
                    </span>
                  </div>
                )}
              </div>
              <span
                className={`shrink-0 rounded-full px-4 py-1.5 text-[1.1rem] font-extrabold ${getScheduleBadgeClass({ isComplete, dueNow })}`}
              >
                {statusText}
              </span>
            </div>

            {questionnaire.id === 'habc' && (
              <>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#b9d8e8] bg-[#f7fbfe] p-4">
                  <div>
                    <div className="text-[1.4rem] font-extrabold text-[#0f3950]">Dashboard</div>
                    <div className="text-[1.15rem] font-semibold text-[#526b7a]">
                      Open HABC trends and latest subscale status.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="vica-btn vica-session-btn-new"
                    onClick={() => setShowDashboard(true)}
                  >
                    Dashboard
                  </button>
                </div>
                <Dialog
                  open={showDashboard}
                  onClose={() => setShowDashboard(false)}
                  fullWidth
                  maxWidth="lg"
                >
                  <div className="bg-gradient-to-b from-[#edf7ff] to-white p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h2 className="m-0 text-[2.2rem] font-extrabold text-[#0f2b3a]">Dashboard</h2>
                        <p className="vica-subtitle !text-[1.25rem]">HABC score trend and subscale status.</p>
                      </div>
                      <button
                        type="button"
                        className="vica-btn-secondary vica-session-btn-new"
                        onClick={() => setShowDashboard(false)}
                      >
                        Close
                      </button>
                    </div>
                    <HabcDashboardChart reports={selectedSchedule.reports} />
                  </div>
                </Dialog>
              </>
            )}

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {selectedSchedule.periods.map((period) => {
                const isNext = nextPeriod?.key === period.key;
                const periodStatus = period.completed
                  ? 'Done'
                  : isNext
                    ? statusText
                    : 'Upcoming';

                return (
                  <div
                    key={period.key}
                    className={`rounded-lg border px-5 py-4 ${getPeriodCardClass({
                      completed: period.completed,
                      isNext,
                      dueNow: isNext && dueNow,
                    })}`}
                  >
                    <div className="text-[1.35rem] font-extrabold text-[#0f3950]">{period.label}</div>
                    <div className="mt-1 text-[1.15rem] font-semibold text-[#526b7a]">{periodStatus}</div>
                    {!period.completed && (
                      <div className="mt-2 text-[1.05rem] text-[#6b7f8d]">
                        Due date: {formatDueDate(period.dueDate)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#dceaf2] pt-4">
              <div className="text-[1.25rem] text-[#526b7a]">
                {nextPeriod ? (
                  <>
                    Current tag: <strong>{nextPeriod.label}</strong>
                  </>
                ) : (
                  'All scheduled assessments are complete.'
                )}
              </div>
              {questionnaire.available ? (
                <button
                  type="button"
                  className="rounded-full bg-green-600 px-14 py-5 text-[1.6rem] font-extrabold text-white shadow-xl shadow-green-200 transition hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isAvailableNow}
                  onClick={() => {
                    setAnswers({});
                    setCurrentQuestion(0);
                    setShowAssessmentLanding(hasQuestionnaireLanding(questionnaire.id));
                    setActiveAssessment({
                      questionnaireId: questionnaire.id,
                      periodKey: nextPeriod.key,
                      periodLabel: nextPeriod.label,
                    });
                  }}
                >
                  {isAvailableNow ? 'Start' : 'Not Due'}
                </button>
              ) : (
                <button type="button" className="vica-btn-secondary vica-btn-md" disabled>
                  Questions coming soon
                </button>
              )}
            </div>

            {selectedSchedule.reports.length > 0 && (
              <div className="mt-5 rounded-xl border border-[#b9d8e8] bg-[#f7fbfe] p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[1.4rem] font-extrabold text-[#0f3950]">Previous reports</div>
                    <div className="text-[1.15rem] font-semibold text-[#526b7a]">
                      Completed assessments and saved scores
                    </div>
                  </div>
                  <span className="rounded-full bg-white px-4 py-1.5 text-[1.1rem] font-extrabold text-[#006298] border border-[#cfe2ee]">
                    {selectedSchedule.reports.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {selectedSchedule.reports.map((report) => {
                    const reportStatus = (questionnaire.id === 'habc' || questionnaire.id === 'gad7')
                      ? getReportStatus(questionnaire.id, report)
                      : null;

                    return (
                      <div
                        key={report.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#dceaf2] bg-white px-4 py-4"
                      >
                        <div className="min-w-0">
                          <div className="text-[1.3rem] font-extrabold text-[#0f3950]">{report.periodLabel || 'Report'}</div>
                          <div className="text-[1.1rem] font-semibold text-[#526b7a]">
                            {new Date(report.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(questionnaire.id === 'habc' || questionnaire.id === 'gad7') && (
                            <span className={`rounded-full border px-4 py-1.5 text-[1.1rem] font-extrabold ${reportStatus.colorClass}`}>
                              {reportStatus.label} · {reportStatus.score}/{getQuestionnaireMaxScore(questionnaire.id)}
                            </span>
                          )}
                          {questionnaire.available && (
                            <button
                              type="button"
                              onClick={() => setViewingReport(report)}
                              className="vica-btn-secondary vica-btn-md !text-[1.1rem]"
                            >
                              View
                            </button>
                          )}
                          {questionnaire.id === 'habc' && (
                            <button
                              type="button"
                              onClick={() => setVisualizingReport(report)}
                              className="vica-btn vica-btn-md !text-[1.1rem]"
                            >
                              Visualize
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      );

      return renderPageShell(detailContent);
    }

    const dashboardContent = (
      <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
        <div className="mb-5">
          <h2 className="m-0 text-2xl font-extrabold text-[#0f2b3a]">Questionnaire</h2>
          <p className="vica-subtitle">
            Complete each questionnaire when its scheduled assessment becomes due.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {assessmentSchedules.map((schedule) => {
            const { questionnaire, nextPeriod, dueNow, statusText } = schedule;
            const isComplete = !nextPeriod;

            return (
              <button
                type="button"
                key={questionnaire.id}
                className="w-full rounded-xl border border-[#cfe2ee] bg-white shadow-sm p-4 text-left transition hover:border-[#006298] hover:bg-[#f7fbfe]"
                onClick={() => setSelectedQuestionnaireId(questionnaire.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="m-0 text-lg font-extrabold text-[#0f3950]">{questionnaire.title}</h3>
                    <p className="m-0 mt-1 text-sm text-[#526b7a]">
                      {nextPeriod ? `Next: ${nextPeriod.label}` : 'All scheduled assessments complete'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!questionnaire.available && (
                      <span className="rounded-full bg-[#eef3f6] px-3 py-1 text-xs font-extrabold text-[#526b7a]">
                        Coming soon
                      </span>
                    )}
                    <span
                      className={`shrink-0 rounded-full px-3 py-1 text-xs font-extrabold ${getScheduleBadgeClass({ isComplete, dueNow })}`}
                    >
                      {statusText}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );

    return renderPageShell(dashboardContent);
  }

  if (showAssessmentLanding && hasQuestionnaireLanding(activeQuestionnaireId)) {
    const isHabc = activeQuestionnaireId === 'habc';
    const isGad7 = activeQuestionnaireId === 'gad7';
    const landingIntro = isHabc ? HABC_INTRO : isGad7 ? GAD7_INTRO : PHQ9_INTRO;
    const scaleMaxScore = getQuestionnaireMaxScore(activeQuestionnaireId);
    const landingContent = (
      <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
        <div className="rounded-xl border border-[#cfe2ee] bg-white p-6 shadow-sm">
          <div className="text-[1.1rem] font-extrabold uppercase tracking-wide text-[#006298]">
            {activeQuestionnaire.title} · {activeAssessment.periodLabel}
          </div>
          <h2 className="m-0 mt-2 text-[2.2rem] font-extrabold text-[#0f2b3a]">
            {isHabc
              ? 'Healthy Aging Brain Care Monitor'
              : isGad7
                ? 'Generalized Anxiety Disorder Scale'
                : 'Patient Health Questionnaire-9'}
          </h2>
          {/* HABC's own intro text covers this; other scales still need a lead-in. */}
          {!isHabc && (
            <p className="mt-2 text-[1.25rem] font-semibold text-[#526b7a]">
              Over the last two weeks, answer each item based on how often the problem has bothered you.
            </p>
          )}

          <div className="mt-5 space-y-3">
            {landingIntro.map((paragraph, paragraphIndex) => {
              // Plain strings (GAD-7 / PHQ-9) and the structured HABC script
              // share this renderer.
              const block =
                typeof paragraph === 'string' ? { parts: [paragraph] } : paragraph;
              const indentClass =
                block.indent === 2 ? 'ml-12' : block.indent === 1 ? 'ml-6' : '';

              return (
                <p
                  key={paragraphIndex}
                  className={`m-0 text-[1.35rem] leading-[2.1rem] text-[#1c3f52] ${indentClass}`}
                >
                  {block.parts.map((part, partIndex) => {
                    if (typeof part === 'string') {
                      return <span key={partIndex}>{part}</span>;
                    }
                    return (
                      <span
                        key={partIndex}
                        className={`${part.bold ? 'font-extrabold' : ''} ${part.underline ? 'underline' : ''}`}
                      >
                        {part.text}
                      </span>
                    );
                  })}
                </p>
              );
            })}
          </div>

          {isHabc && (
            <>
              <div className="mt-5">
                <div className="mb-3 text-[1.3rem] font-extrabold text-[#0f3950]">Response scale</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {HABC_RESPONSE_GUIDE.map((guide) => (
                    <div
                      key={guide.value}
                      className={`flex min-h-[6rem] flex-col items-center justify-center rounded-lg border px-4 py-3 text-center ${HABC_RESPONSE_COLOR_CLASSES[guide.value]}`}
                    >
                      <div className="text-[1.2rem] font-extrabold">{guide.label}</div>
                      <div className="mt-1 text-[1.1rem] font-semibold">{guide.days}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="mb-3 text-[1.3rem] font-extrabold text-[#0f3950]">Subscales</div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {HABC_SUBSCALE_EXPLANATIONS.map((subscale) => (
                    <div
                      key={subscale.title}
                      className="rounded-lg border border-[#dceaf2] bg-[#f7fbfe] px-4 py-3"
                    >
                      <div className="text-[1.3rem] font-extrabold text-[#0f2b3a]">{subscale.title}</div>
                      <div className="mt-1 text-[1.2rem] leading-8 text-[#1c3f52]">{subscale.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="mb-3 text-[1.3rem] font-extrabold text-[#0f3950]">Total score colors</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {HABC_SCORE_BANDS.map((band) => (
                    <div
                      key={band.range}
                      className={`flex min-h-[6rem] flex-col items-center justify-center rounded-lg border px-4 py-3 text-center ${HABC_RESPONSE_COLOR_CLASSES[HABC_BAND_COLOR_VALUE[band.color]]}`}
                    >
                      <div className="text-[1.6rem] font-extrabold">{band.range}</div>
                      <div className="mt-1 text-[1.2rem] font-semibold">{band.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {isGad7 && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {GAD7_SCORE_BANDS.map((band) => (
                <div key={band.range} className={`rounded-lg border px-4 py-3 ${getGad7BandColor(band.color)}`}>
                  <div className="text-[1.6rem] font-extrabold">{band.range}</div>
                  <div className="mt-1 text-[1.2rem] font-semibold">{band.label}</div>
                </div>
              ))}
            </div>
          )}

          {!isHabc && !isGad7 && (
            <div className="mt-5 rounded-lg border border-[#dceaf2] bg-[#f7fbfe] px-4 py-3">
              <div className="text-lg font-extrabold text-[#004f79]">0-{scaleMaxScore}</div>
              <div className="mt-1 text-sm font-semibold text-[#526b7a]">Total score range</div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              className="rounded-full bg-green-600 px-14 py-5 text-[1.6rem] font-extrabold text-white shadow-xl shadow-green-200 transition hover:bg-green-700"
              onClick={() => setShowAssessmentLanding(false)}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );

    return renderPageShell(landingContent);
  }

  if (isNpiAssessment && currentNpiSection) {
    const presentKey = npiKey(currentQuestion, 'present');
    const convincedKey = npiKey(currentQuestion, 'convinced');
    const frequencyKey = npiKey(currentQuestion, 'frequency');
    const severityKey = npiKey(currentQuestion, 'severity');
    const distressKey = npiKey(currentQuestion, 'distress');
    const presentValue = answers[presentKey];
    const convincedValue = answers[convincedKey];
    const currentSectionComplete = isNpiSectionComplete(currentNpiSection, answers, currentQuestion);

    const renderOptionGroup = (title, key, options, gridClass = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3') => (
      <div className="mt-5">
        <div className="mb-2 text-sm font-extrabold text-[#0f3950]">{title}</div>
        <div className={`grid ${gridClass} gap-3`}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${
                answers[key] === option.value
                  ? 'border-[#006298] bg-[#dff0fc] text-[#06354f]'
                  : 'border-[#d7e3ea] bg-white text-[#1c3f52] hover:border-[#006298]'
              }`}
              onClick={() => setAnswerValue(key, option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );

    const npiContent = (
      <>
        <div className="p-4 bg-gradient-to-b from-[#edf7ff] to-white">
          <div className="mb-4">
            <div>
              <div className="text-sm font-extrabold uppercase tracking-wide text-[#006298]">
                NPI · {activeAssessment.periodLabel}
              </div>
              <div className="text-sm text-[#526b7a]">
                Section {currentQuestion + 1} of {NPI_SECTIONS.length}
              </div>
            </div>
          </div>

          <div className="mb-5">
            <div className="mb-1 flex items-center justify-between text-xs font-extrabold text-[#006298]">
              <span>Questions answered progress</span>
              <span>{progressPercent}%</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full border border-[#b7ddf1] bg-[#e7f3fb]">
              <div
                className="h-full rounded-full bg-[#006298] transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="rounded-xl border border-[#cfe2ee] bg-white p-5 shadow-sm">
            <div className="text-xs font-extrabold uppercase tracking-wide text-[#006298]">
              Section explanation
            </div>
            <h2 className="m-0 mt-2 text-2xl font-extrabold text-[#0f2b3a]">
              {currentNpiSection.title}
            </h2>
            <p className="mt-3 text-[15px] leading-7 text-[#1c3f52]">
              {currentNpiSection.explanation}
            </p>

            {renderOptionGroup(
              currentNpiSection.screener || `Has the patient had ${currentNpiSection.title.toLowerCase()} symptoms in the past four weeks?`,
              presentKey,
              NPI_PRESENT_OPTIONS,
              'grid-cols-1 sm:grid-cols-2'
            )}

            {presentValue === 1 && (
              <>
                {currentNpiSection.convictionQuestion && renderOptionGroup(
                  currentNpiSection.convictionQuestion,
                  convincedKey,
                  NPI_PRESENT_OPTIONS,
                  'grid-cols-1 sm:grid-cols-2'
                )}

                {(!currentNpiSection.convictionQuestion || convincedValue === 1) && (
                  <>
                    {(currentNpiSection.followUpItems || []).length > 0 && (
                      <div className="mt-5">
                        <div className="mb-2 text-sm font-extrabold text-[#0f3950]">
                          Please answer each {currentNpiSection.title} item
                        </div>
                        <div className="space-y-3">
                          {currentNpiSection.followUpItems.map((item, itemIndex) => (
                            <div
                              key={item}
                              className="rounded-xl border border-[#d7e3ea] bg-[#fbfdff] p-4"
                            >
                              <div className="text-sm font-bold text-[#1c3f52]">{item}</div>
                              <div className="mt-3 grid grid-cols-2 gap-3">
                                {NPI_PRESENT_OPTIONS.map((option) => {
                                  const key = npiFollowUpKey(currentQuestion, itemIndex);
                                  return (
                                    <button
                                      key={option.value}
                                      type="button"
                                      className={`rounded-lg border px-4 py-2 text-left text-sm font-bold transition ${
                                        answers[key] === option.value
                                          ? 'border-[#006298] bg-[#dff0fc] text-[#06354f]'
                                          : 'border-[#d7e3ea] bg-white text-[#1c3f52] hover:border-[#006298]'
                                      }`}
                                      onClick={() => setAnswerValue(key, option.value)}
                                    >
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {renderOptionGroup(
                      currentNpiSection.frequencyQuestion || 'How often does this behavior occur?',
                      frequencyKey,
                      NPI_FREQUENCY_OPTIONS
                    )}
                    <div className="mt-5">
                      {renderOptionGroup(
                        currentNpiSection.severityQuestion || 'How severe would you say it is?',
                        severityKey,
                        NPI_SEVERITY_OPTIONS
                      )}
                      {(currentNpiSection.severityDescriptions || []).length > 0 && (
                        <div className="mt-3 rounded-lg border border-[#dceaf2] bg-[#f7fbfe] px-4 py-3">
                          {currentNpiSection.severityDescriptions.map((description) => (
                            <p key={description} className="m-0 text-sm leading-6 text-[#526b7a]">
                              {description}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    {renderOptionGroup(
                      currentNpiSection.distressQuestion || 'How emotionally distressing do you find this behavior?',
                      distressKey,
                      NPI_DISTRESS_OPTIONS
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="p-4 text-right flex justify-between">
          <button
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
            className="vica-btn-secondary vica-btn-md"
          >
            Back
          </button>
          {currentQuestion < NPI_SECTIONS.length - 1 ? (
            <button
              onClick={() => setCurrentQuestion(currentQuestion + 1)}
              disabled={!currentSectionComplete}
              className="vica-btn vica-btn-md"
            >
              Next Section
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!currentSectionComplete}
              className="vica-btn vica-btn-md"
            >
              Submit Report
            </button>
          )}
        </div>
      </>
    );

    return renderPageShell(npiContent);
  }

  const formContent = (
    <>
      <div className="p-6 md:p-8 bg-gradient-to-b from-[#edf7ff] to-white">
        <div className="mb-5">
          <div>
            <div className="text-[1.45rem] font-extrabold uppercase tracking-wide text-[#006298]">
              {activeQuestionnaire.title} · {activeAssessment.periodLabel}
            </div>
            <div className="mt-1 text-[1.6rem] font-semibold text-[#526b7a]">
              Question {currentQuestion + 1} of {totalQuestions}
            </div>
          </div>
        </div>
        <div className="mb-7">
          <div className="mb-2 flex items-center justify-between text-[1.4rem] font-extrabold text-[#006298]">
            <span>Questions answered progress</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-5 overflow-hidden rounded-full border border-[#b7ddf1] bg-[#e7f3fb]">
            <div
              className="h-full rounded-full bg-[#006298] transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
        {curPrompt && (
          <p className="mb-4 text-base font-semibold text-[#526b7a]">{curPrompt}</p>
        )}
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="text-2xl md:text-3xl font-bold leading-snug m-0">{curText}</h2>

          <button
            type="button"
            className="vica-btn-secondary vica-session-btn-new shrink-0"
            onClick={() => {
              stopReading();
              readCurrentQuestion();
            }}
            aria-label="Read this question aloud again"
            title="Read again"
          >
            🔊 Read
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {responseOptions.map((opt) => {
            const isSelected = answers[curKey] === opt.value;

            return (
              <label
                key={opt.value}
                className={`flex items-center p-6 border rounded-xl cursor-pointer transition text-[1.4rem] font-extrabold ${getStandardResponseClass(
                  activeQuestionnaireId,
                  isSelected,
                  opt.value
                )}`}
              >
                <input
                  type="radio"
                  name="answer"
                  value={opt.value}
                  checked={isSelected}
                  onChange={handleChange(curKey)}
                  className="mr-4 h-7 w-7 accent-current"
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </div>
      {!viewingReport && (
        <div className="p-6 md:p-8 text-right flex justify-between">
          <button
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
            className="vica-btn-secondary vica-tour-btn"
          >
            Back
          </button>
          {currentQuestion < totalQuestions - 1 ? (
            <button
              onClick={() => setCurrentQuestion(currentQuestion + 1)}
              disabled={answers[curKey] == null}
              className="vica-btn vica-tour-btn-primary"
            >
              Next
            </button>
          ) : (
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={handleSubmit}
                disabled={answers[curKey] == null || isSubmitting}
                className="vica-btn vica-tour-btn-primary"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
              {submitError && (
                <p className="m-0 max-w-sm text-right text-[1.1rem] font-semibold text-red-700">
                  {submitError}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  return renderFormShell(formContent);
}